import React, { useContext, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import {
  Box,
  Divider,
  Typography,
  Icon,
  Autocomplete,
  TextField,
  Grid,
  Stack,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  createFilterOptions,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";

import { profitAnalysisApi } from "./api/profitAnalysisApi";
import { useJobs } from "./hooks/useJobs";
import { useRunningTotals } from "./hooks/useRunningTotals";
import { useUnsavedChanges } from "./hooks/useUnsavedChanges";
import Header from "../../components/Header";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import CommentIcon from "@mui/icons-material/Comment";
import ConstructionIcon from "@mui/icons-material/Construction";
import CabinIcon from "@mui/icons-material/Cabin";
import LandscapeIcon from "@mui/icons-material/Landscape";
import TuneIcon from "@mui/icons-material/Tune";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import GavelIcon from "@mui/icons-material/Gavel";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { PercentField } from "./fields/PercentField";
import { SafeDatePicker } from "./fields/SafeDatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { numberWithCommas } from "../../utils/formatMoney";
import { generatePDF, pdfProps } from "../../utils/generatePDF";
import { KpiSidebar } from "./components/layout/KpiSidebar";
import { SectionNav } from "./components/layout/SectionNav";
import { MobileShell } from "./components/layout/MobileShell";
import { MobileSectionPickerItem } from "./components/layout/MobileSectionPicker";
import { ContractSection } from "./components/sections/ContractSection";
import { LaborSection } from "./components/sections/LaborSection";
import { MaterialSection } from "./components/sections/MaterialSection";
import { LotExtrasSection } from "./components/sections/LotExtrasSection";
import { OptionsSection } from "./components/sections/OptionsSection";
import { SoftCostSection } from "./components/sections/SoftCostSection";
import { ForwardCommitmentSection } from "./components/sections/ForwardCommitmentSection";
import { useAccount, useMsal } from "@azure/msal-react";
import moment from "moment";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  ContractSectionProps,
  GenInfoProps,
  JobProps,
  SoftCostSectionProps,
  ForwardCommitmentSectionProps,
  ChangeCountContractSectionProps,
  LaborSectionProps,
  ChangeCountLaborSectionProps,
  MaterialSectionProps,
  ChangeCountMaterialSectionProps,
  LotExtraSectionProps,
  ChangeCountLotExtraSectionProps,
  OptionsSectionProps,
  ChangeCountOptionsSectionProps,
  ChangeCountSoftCostSectionProps,
  ChangeCountForwardCommitmentSectionProps,
} from "../../types";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { grey } from "@mui/material/colors";
import { UIContext } from "../../providers/UIProvider";
import {
  ProfitAnalysisDetails,
  ProfitAnalysisNote,
  ProjectedCloseDateChange,
  ExitStrategyChange,
} from "../../models/ProfitAnalysisData";

export type PAType = "spec" | "sold";
export type PAMode = "create" | "edit" | "view";

export interface ProfitAnalysisPageProps {
  paType: PAType;
  mode: PAMode;
}

const drawerWidth = 240;

interface FormErrors {
  [key: string]: string;
  BrochurePrice: string;
  LotPremium: string;
  OptionalArchitecturalPlanChanges: string;
  CustomArchitecturalPlanChanges: string;
  BuyerSelectedOptions: string;
  OptionsGiven: string;
  OptionsRetailValue: string;
  CashOptionsPaid: string;
  SalesAllowanceOptions: string;
  SalesAllowanceClosingCosts: string;
  SalesAllowancePriceReduced: string;
  LaborBudget: string;
  ExtraLaborActual: string;
  SpecialLabor: string;
  MaterialBudget: string;
  ExtraMaterialBudget: string;
  SpecialMaterial: string;
  LotExtras: string;
  CostOfOptionsWithContract: string;
  LotCost: string;
  Discretionary: string;
  RealtorCommissions: string;
  SpecialMarketingIncentive: string;
  RealtorCommissionsadjusted: string;
  InHouseCommissions: string;
  InterimInterestBudget: string;
  ClosingCostBudget: string;
  BuilderBonus: string;
}

type SpecFormState = {
  genInfo: GenInfoProps;
  contractSection: ContractSectionProps;
  laborSection: { LaborBudget: number | undefined; ExtraLaborActual: number | undefined; SpecialLabor: number | undefined };
  materialSection: { MaterialBudget: number | undefined; ExtraMaterialBudget: number | undefined; SpecialMaterial: number | undefined };
  lotExtraSection: { LotExtras: number | undefined };
  optionsSection: { CostOfOptionsWithContract: number | undefined };
  softCostSection: SoftCostSectionProps;
  notesList: Array<ProfitAnalysisNote>;
};

type SoldFormState = SpecFormState & {
  forwardCommitmentSection: ForwardCommitmentSectionProps;
};

const emptyFormErrors: FormErrors = {
  BrochurePrice: "",
  LotPremium: "",
  OptionalArchitecturalPlanChanges: "",
  CustomArchitecturalPlanChanges: "",
  BuyerSelectedOptions: "",
  OptionsGiven: "",
  OptionsRetailValue: "",
  CashOptionsPaid: "",
  SalesAllowanceOptions: "",
  SalesAllowanceClosingCosts: "",
  SalesAllowancePriceReduced: "",
  LaborBudget: "",
  ExtraLaborActual: "",
  SpecialLabor: "",
  MaterialBudget: "",
  ExtraMaterialBudget: "",
  SpecialMaterial: "",
  LotExtras: "",
  CostOfOptionsWithContract: "",
  LotCost: "",
  Discretionary: "",
  RealtorCommissions: "",
  SpecialMarketingIncentive: "",
  RealtorCommissionsadjusted: "",
  InHouseCommissions: "",
  InterimInterestBudget: "",
  ClosingCostBudget: "",
  BuilderBonus: "",
};

export function ProfitAnalysisPage({ paType, mode }: ProfitAnalysisPageProps) {
  const { accounts } = useMsal();
  const [, dispatch]: any = useContext(UIContext);
  const { id } = useParams();
  const account = useAccount(accounts[0]);
  const navigate = useNavigate();
  const location = useLocation();

  // ── create-only: location state ────────────────────────────────────────────
  const [locationStateSubdivision] = React.useState<string | null>(
    mode === "create" ? (location?.state?.subdivision || null) : null
  );
  const [locationStateJobAddress] = React.useState<string>(
    mode === "create" ? (location?.state?.jobAddress || "") : ""
  );

  // ── create-only: jobs autocomplete ─────────────────────────────────────────
  const { jobs, loading: jobsLoading } = useJobs();
  const [selectedJob, setSelectedJob] = React.useState<JobProps | undefined>(undefined);
  const subdivisionRef = React.useRef<HTMLDivElement>(null);

  // ── local sub-mode: "isReviewing" replaces the old mode === "review" ────────
  // Used by create and edit pages to show the confirmation action bar.
  const [isReviewing, setIsReviewing] = React.useState(false);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const minDate = moment();

  // ── genInfo ─────────────────────────────────────────────────────────────────
  const [genInfo, setGenInfo] = React.useState<GenInfoProps>({
    Subdivision: mode === "create" ? (locationStateSubdivision || null) : "",
    StreetAddress: mode === "create" ? (locationStateJobAddress || "") : "",
    JobId: mode === "create" ? null : 0,
    ProjectId: mode === "create" ? null : 0,
    ProjectNumber: "",
    StageCode: "",
    JobNumber: "",
    Buyer: "",
    PlanElevationNumber: "",
    PlanNumber: "",
    JobType: "",
    ExitStrategy: mode === "create" ? undefined : 0,
    DateSold: mode === "create" ? null : (moment() as any),
    ProjectedCloseDate: mode === "create" ? moment().startOf("day") : moment().startOf("day"),
    CurrentUser: account.name as string,
    SalesPrice: mode === "create" ? null : 0,
    CloseDate: mode === "view" ? moment("0001-01-01T00:00:00") : null,
    ProfitAnalysisId: mode === "create" ? null : 0,
    BPPercent: 0,
    BLPercent: 0,
    DiscountToBP: 0,
    DiscountToBL: 0,
    SalesPriceAtBL: 0,
    SalesPriceAtBP: 0,
  });

  // ── section state ───────────────────────────────────────────────────────────
  const [contractSection, setContractSection] = React.useState<ContractSectionProps>({
    BrochurePrice: mode === "create" ? undefined : 0,
    LotPremium: mode === "create" ? undefined : 0,
    OptionalArchitecturalPlanChanges: mode === "create" ? undefined : 0,
    CustomArchitecturalPlanChanges: mode === "create" ? undefined : 0,
    BuyerSelectedOptions: mode === "create" ? undefined : 0,
    OptionsGiven: mode === "create" ? undefined : 0,
    OptionsRetailValue: mode === "create" ? undefined : 0,
    CashOptionsPaid: mode === "create" ? undefined : 0,
    SalesAllowanceOptions: mode === "create" ? undefined : 0,
    SalesAllowanceClosingCosts: mode === "create" ? undefined : 0,
    SalesAllowancePriceReduced: mode === "create" ? undefined : 0,
  });

  const [laborSection, setLaborSection] = React.useState<{
    LaborBudget: number | undefined;
    ExtraLaborActual: number | undefined;
    SpecialLabor: number | undefined;
  }>({
    LaborBudget: mode === "create" ? undefined : 0,
    ExtraLaborActual: mode === "create" ? undefined : 0,
    SpecialLabor: mode === "create" ? undefined : 0,
  });

  const [materialSection, setMaterialSection] = React.useState<{
    MaterialBudget: number | undefined;
    ExtraMaterialBudget: number | undefined;
    SpecialMaterial: number | undefined;
  }>({
    MaterialBudget: mode === "create" ? undefined : 0,
    ExtraMaterialBudget: mode === "create" ? undefined : 0,
    SpecialMaterial: mode === "create" ? undefined : 0,
  });

  const [lotExtraSection, setLotExtraSection] = React.useState<{ LotExtras: number | undefined }>({
    LotExtras: mode === "create" ? undefined : 0,
  });

  const [optionsSection, setOptionsSection] = React.useState<{ CostOfOptionsWithContract: number | undefined }>({
    CostOfOptionsWithContract: mode === "create" ? undefined : 0,
  });

  const [softCostSection, setSoftCostSection] = React.useState<SoftCostSectionProps>({
    LotCost: 0,
    Discretionary: 0,
    HasRealtor: true,
    RealtorCommissions: 0,
    SpecialMarketingIncentive: 0,
    RealtorCommissionsadjusted: 0,
    InHouseCommissions: 0,
    InterimInterestBudget: 0,
    ClosingCostBudget: 0,
    BuilderBonus: 0,
  });

  // ── sold-only: forward commitment ───────────────────────────────────────────
  const [forwardCommitmentSection, setForwardCommitmentSection] = React.useState<ForwardCommitmentSectionProps>({
    HasForwardCommitment: false,
    ForwardCommitmentExpiryDate: moment().startOf("day"),
    ForwardCommitmentBatchNumber: "",
  });

  // ── edit/view: initial + change-count mirrors for sections ──────────────────
  const [initialContractSection, setInitialContractSection] = React.useState<ContractSectionProps>({
    BrochurePrice: 0, LotPremium: 0, OptionalArchitecturalPlanChanges: 0,
    CustomArchitecturalPlanChanges: 0, BuyerSelectedOptions: 0, OptionsGiven: 0,
    OptionsRetailValue: 0, CashOptionsPaid: 0, SalesAllowanceOptions: 0,
    SalesAllowanceClosingCosts: 0, SalesAllowancePriceReduced: 0,
  });
  const [changeCountContractSection, setChangeCountContractSection] = React.useState<ChangeCountContractSectionProps>({
    BrochurePrice: 0, LotPremium: 0, OptionalArchitecturalPlanChanges: 0,
    CustomArchitecturalPlanChanges: 0, BuyerSelectedOptions: 0, OptionsGiven: 0,
    OptionsRetailValue: 0, CashOptionsPaid: 0, SalesAllowanceOptions: 0,
    SalesAllowanceClosingCosts: 0, SalesAllowancePriceReduced: 0,
  });
  const [initialLaborSection, setInitialLaborSection] = React.useState<LaborSectionProps>({
    LaborBudget: 0, ExtraLaborActual: 0, SpecialLabor: 0,
  });
  const [changeCountLaborSection, setChangeCountLaborSection] = React.useState<ChangeCountLaborSectionProps>({
    LaborBudget: 0, ExtraLaborActual: 0, SpecialLabor: 0,
  });
  const [initialMaterialSection, setInitialMaterialSection] = React.useState<MaterialSectionProps>({
    MaterialBudget: 0, ExtraMaterialBudget: 0, SpecialMaterial: 0,
  });
  const [changeCountMaterialSection, setChangeCountMaterialSection] = React.useState<ChangeCountMaterialSectionProps>({
    MaterialBudget: 0, ExtraMaterialBudget: 0, SpecialMaterial: 0,
  });
  const [initialLotExtraSection, setInitialLotExtraSection] = React.useState<LotExtraSectionProps>({ LotExtras: 0 });
  const [changeCountLotExtraSection, setChangeCountLotExtraSection] = React.useState<ChangeCountLotExtraSectionProps>({ LotExtras: 0 });
  const [initialOptionsSection, setInitialOptionsSection] = React.useState<OptionsSectionProps>({ CostOfOptionsWithContract: 0 });
  const [changeCountOptionsSection, setChangeCountOptionsSection] = React.useState<ChangeCountOptionsSectionProps>({ CostOfOptionsWithContract: 0 });
  const [initialSoftCostSection, setInitialSoftCostSection] = React.useState<SoftCostSectionProps>({
    LotCost: 0, Discretionary: 0, HasRealtor: true, RealtorCommissions: 0,
    SpecialMarketingIncentive: 0, RealtorCommissionsadjusted: 0, InHouseCommissions: 0,
    InterimInterestBudget: 0, ClosingCostBudget: 0, BuilderBonus: 0,
  });
  const [changeCountSoftCostSection, setChangeCountSoftCostSection] = React.useState<ChangeCountSoftCostSectionProps>({
    LotCost: 0, Discretionary: 0, HasRealtor: 1, RealtorCommissions: 0,
    SpecialMarketingIncentive: 0, RealtorCommissionsadjusted: 0, InHouseCommissions: 0,
    InterimInterestBudget: 0, ClosingCostBudget: 0, BuilderBonus: 0,
  });
  // sold-only initial/changeCount for forward commitment
  const [initialForwardCommitmentSection, setInitialForwardCommitmentSection] = React.useState<ForwardCommitmentSectionProps>({
    HasForwardCommitment: false,
    ForwardCommitmentExpiryDate: moment().startOf("day"),
    ForwardCommitmentBatchNumber: "",
  });
  const [changeCountForwardCommitmentSection, setChangeCountForwardCommitmentSection] = React.useState<ChangeCountForwardCommitmentSectionProps>({
    HasForwardCommitment: 0, ForwardCommitmentExpiryDate: 0, ForwardCommitmentBatchNumber: 0,
  });

  // ── helper strings for display (lot premium / market adjustment raw values) ─
  const [lotPremium, setLotPremium] = React.useState<string>("");
  const [marketAdjustment, setMarketAdjustment] = React.useState<string>("");

  // ── notes ───────────────────────────────────────────────────────────────────
  const [note, setNote] = React.useState<string>("");
  const [notesList, setNotesList] = React.useState<Array<ProfitAnalysisNote>>([]);
  const [notesExpanded, setNotesExpanded] = React.useState<boolean>(true);

  // ── edit/view: change history ───────────────────────────────────────────────
  const [changeHistoryExpanded, setChangeHistoryExpanded] = React.useState<boolean>(false);
  const [projectedCloseDateChanges, setProjectedCloseDateChanges] = React.useState<ProjectedCloseDateChange[]>([]);
  const [exitStrategyChanges, setExitStrategyChanges] = React.useState<ExitStrategyChange[]>([]);

  // ── edit-only: track which fields changed for local change-history records ──
  const [exitStrategyFieldUpdated, setExitStrategyFieldUpdated] = React.useState(false);
  const [projectedCloseDateFieldUpdated, setProjectedCloseDateFieldUpdated] = React.useState(false);

  // ── PDF data ─────────────────────────────────────────────────────────────────
  const [pdfData, setPdfData] = React.useState<pdfProps | undefined>(undefined);

  // ── form errors / validation ─────────────────────────────────────────────────
  const [formErrors, setFormErrors] = React.useState<FormErrors>(emptyFormErrors);
  const [exitStrategyError, setExitStrategyError] = React.useState(false);
  // spec-only percent errors (SoldCreate/Edit do not render BP%/BL% fields)
  const [bpPercentError, setBpPercentError] = React.useState(false);
  const [blPercentError, setBlPercentError] = React.useState(false);

  // ── create-only: form reset flag ─────────────────────────────────────────────
  const [formReset, setFormReset] = React.useState(false);

  // ── edit/view: initial form state (drives unsaved-changes detection) ─────────
  const [initialFormState, setInitialFormState] = React.useState<SpecFormState | SoldFormState | null>(null);

  // ── dialog state (edit) ──────────────────────────────────────────────────────
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [showPrintWarning, setShowPrintWarning] = useState(false);
  const [printChoice, setPrintChoice] = useState<"saveAndPrint" | "printOnly">("saveAndPrint");

  // ── unsaved changes (edit only) ──────────────────────────────────────────────
  const currentFormState: SpecFormState | SoldFormState = paType === "sold"
    ? { genInfo, contractSection, laborSection, materialSection, lotExtraSection, optionsSection, softCostSection, forwardCommitmentSection, notesList }
    : { genInfo, contractSection, laborSection, materialSection, lotExtraSection, optionsSection, softCostSection, notesList };

  const { hasChanges: hasUnsavedChanges } = useUnsavedChanges(
    mode === "edit" ? initialFormState : null,
    currentFormState
  );

  // ── navbar items ─────────────────────────────────────────────────────────────
  const navbarItems = React.useMemo(() => {
    const base = [
      { id: 0, section: "Contract", href: "#contract", icon: <TextSnippetIcon /> },
      { id: 1, section: "Labor", href: "#labor", icon: <ConstructionIcon /> },
      { id: 2, section: "Material", href: "#material", icon: <CabinIcon /> },
      { id: 3, section: "Lot Extras", href: "#lotExtras", icon: <LandscapeIcon /> },
      { id: 4, section: "Options", href: "#options", icon: <TuneIcon /> },
      { id: 5, section: "Soft Cost", href: "#softCost", icon: <RequestQuoteIcon /> },
      ...(paType === "sold" ? [{ id: 6, section: "Forward Commitment", href: "#forwardCommitment", icon: <GavelIcon /> }] : []),
      { id: 7, section: "Notes", href: "#notes", icon: <CommentIcon /> },
    ];
    if (mode === "edit" || mode === "view") {
      return [
        { id: -1, section: "Change History", href: "#changeHistory", icon: <ChangeHistoryIcon /> },
        ...base,
      ];
    }
    return base;
  }, [paType, mode]);

  // ── updateField ──────────────────────────────────────────────────────────────
  const updateField = (field: keyof FormErrors, value: string) => {
    switch (field) {
      case "BrochurePrice":
        setContractSection({ ...contractSection, BrochurePrice: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "LotPremium":
        setLotPremium(value);
        setContractSection((prev: any) => ({ ...prev, LotPremium: value === "" ? undefined : parseInt(value) }));
        break;
      case "OptionalArchitecturalPlanChanges":
        setContractSection({ ...contractSection, OptionalArchitecturalPlanChanges: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "CustomArchitecturalPlanChanges":
        setContractSection({ ...contractSection, CustomArchitecturalPlanChanges: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "BuyerSelectedOptions":
        setContractSection({ ...contractSection, BuyerSelectedOptions: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "OptionsGiven":
        setContractSection({ ...contractSection, OptionsGiven: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "OptionsRetailValue":
        setContractSection({ ...contractSection, OptionsRetailValue: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "CashOptionsPaid":
        setContractSection({ ...contractSection, CashOptionsPaid: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "SalesAllowanceOptions":
        setContractSection({ ...contractSection, SalesAllowanceOptions: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "SalesAllowanceClosingCosts":
        setContractSection({ ...contractSection, SalesAllowanceClosingCosts: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "SalesAllowancePriceReduced":
        setContractSection({ ...contractSection, SalesAllowancePriceReduced: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "LaborBudget":
        setLaborSection({ ...laborSection, LaborBudget: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "ExtraLaborActual":
        setLaborSection({ ...laborSection, ExtraLaborActual: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "SpecialLabor":
        setLaborSection({ ...laborSection, SpecialLabor: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "MaterialBudget":
        setMaterialSection({ ...materialSection, MaterialBudget: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "ExtraMaterialBudget":
        setMaterialSection({ ...materialSection, ExtraMaterialBudget: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "SpecialMaterial":
        setMaterialSection({ ...materialSection, SpecialMaterial: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "LotExtras":
        setLotExtraSection({ ...lotExtraSection, LotExtras: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "CostOfOptionsWithContract":
        setOptionsSection({ ...optionsSection, CostOfOptionsWithContract: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "LotCost":
        setSoftCostSection({ ...softCostSection, LotCost: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "Discretionary":
        setSoftCostSection({ ...softCostSection, Discretionary: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "RealtorCommissions":
        setSoftCostSection({ ...softCostSection, RealtorCommissions: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "SpecialMarketingIncentive":
        setMarketAdjustment(value);
        setSoftCostSection({ ...softCostSection, SpecialMarketingIncentive: value === "" ? undefined : parseInt(value) });
        break;
      case "RealtorCommissionsadjusted":
        setSoftCostSection({ ...softCostSection, RealtorCommissionsadjusted: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "InHouseCommissions":
        setSoftCostSection({ ...softCostSection, InHouseCommissions: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "InterimInterestBudget":
        setSoftCostSection({ ...softCostSection, InterimInterestBudget: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "ClosingCostBudget":
        setSoftCostSection({ ...softCostSection, ClosingCostBudget: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      case "BuilderBonus":
        setSoftCostSection({ ...softCostSection, BuilderBonus: value === "" ? undefined : Math.abs(parseInt(value)) });
        break;
      default:
        break;
    }

    if (field === "SpecialMarketingIncentive") {
      if (parseInt(value) < -9999999.99 || parseInt(value) > 9999999.99) {
        setFormErrors((prev) => ({ ...prev, [field]: "Enter a number between -10 Million and 10 Million" }));
      }
    } else if (
      value === null ||
      value === undefined ||
      isNaN(parseInt(value === "" ? "0" : value)) ||
      (parseInt(value) < 0 && field !== "LotPremium") ||
      (parseInt(value) < -9999999.9999 && field === "LotPremium") ||
      parseInt(value) > 9999999.9999
    ) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: field === "LotPremium"
          ? "Enter a number between -10 Million and 10 Million"
          : "Enter a number between 0 and 10 Million",
      }));
    } else {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // ── validateState ────────────────────────────────────────────────────────────
  function validateState(state: any): boolean {
    for (const key in state) {
      if (Object.prototype.hasOwnProperty.call(state, key)) {
        if (mode === "create") {
          if (
            (key === "ProfitAnalysisId" && state[key] === null) ||
            (key === "DateSold" && (state[key] === null || state[key] === undefined)) ||
            (key === "SalesPrice" && (state[key] === null || state[key] === undefined)) ||
            (key === "SalesPriceAtBP" && (state[key] === null || state[key] === undefined)) ||
            (key === "SalesPriceAtBL" && (state[key] === null || state[key] === undefined)) ||
            (key === "DiscountToBP" && (state[key] === null || state[key] === undefined)) ||
            (key === "DiscountToBL" && (state[key] === null || state[key] === undefined))
          ) {
            continue;
          }
          if (state[key] === null || state[key] === undefined) return false;
          if (
            key === "ProjectedCloseDate" &&
            ((typeof state[key].isValid === "function" && !state[key].isValid()) ||
              state[key].year() === "NaN" ||
              state[key].year() < 1900 ||
              state[key].year() >= 2100 ||
              state[key]?.isBefore(moment(), "day"))
          ) {
            return false;
          }
          if (key === "contractSection" && (state[key]?.BrochurePrice === undefined || state[key]?.BrochurePrice === 0)) {
            return false;
          }
        } else {
          // edit / view
          if (
            ((state[key] === null || state[key] === undefined) && key !== "CloseDate") ||
            (key === "ProjectedCloseDate" &&
              ((typeof state[key].isValid === "function" && !state[key].isValid()) ||
                state[key].year() === "NaN" ||
                state[key].year() < 1900 ||
                state[key].year() >= 2100 ||
                state[key]?.isBefore(moment(), "day"))) ||
            (key === "CloseDate" &&
              state[key] !== null &&
              ((typeof state[key].isValid === "function" && !state[key].isValid()) ||
                state[key].year() === "NaN" ||
                state[key].year() != 1))
          ) {
            return false;
          }
        }
      }
    }
    return true;
  }

  const checkFormValidity = () => {
    const hasError = Object.values(formErrors).some((e) => e !== "");
    const percentErrors = paType === "spec"
      ? (!exitStrategyError && !bpPercentError && !blPercentError)
      : !exitStrategyError;
    return !hasError && validateState(genInfo) && percentErrors;
  };

  // ── buildPdfData helper ──────────────────────────────────────────────────────
  const buildPdfData = (data: any): pdfProps => ({
    brochurePrice: data.brochurePrice ?? contractSection.BrochurePrice,
    builderBonus: data.builderBonus ?? softCostSection.BuilderBonus,
    buyer: data.buyer ?? genInfo.Buyer,
    buyerSelectedOptions: data.buyerSelectedOptions ?? contractSection.BuyerSelectedOptions,
    cashOptionsPaid: data.cashOptionsPaid ?? contractSection.CashOptionsPaid,
    closingCostBudget: data.closingCostBudget ?? softCostSection.ClosingCostBudget,
    costOfOptionsWithContract: data.costOfOptionsWithContract ?? optionsSection.CostOfOptionsWithContract,
    customArchitecturalPlanChanges: data.customArchitecturalPlanChanges ?? contractSection.CustomArchitecturalPlanChanges,
    dateSold: data.dateSold,
    discretionary: data.discretionary ?? softCostSection.Discretionary,
    exitStrategy: data.exitStrategy ?? genInfo.ExitStrategy,
    extraLaborActual: data.extraLaborActual ?? laborSection.ExtraLaborActual,
    extraMaterialBudget: data.extraMaterialBudget ?? materialSection.ExtraMaterialBudget,
    inHouseCommissions: data.inHouseCommissions ?? softCostSection.InHouseCommissions,
    interimInterestBudget: data.interimInterestBudget ?? softCostSection.InterimInterestBudget,
    jobId: data.jobId ?? genInfo.JobId,
    jobNumber: data.jobNumber ?? genInfo.JobNumber,
    laborBudget: data.laborBudget ?? laborSection.LaborBudget,
    lotCost: data.lotCost ?? softCostSection.LotCost,
    lotExtras: data.lotExtras ?? lotExtraSection.LotExtras,
    lotPremium: data.lotPremium ?? contractSection.LotPremium,
    materialBudget: data.materialBudget ?? materialSection.MaterialBudget,
    optionalArchitecturalPlanChanges: data.optionalArchitecturalPlanChanges ?? contractSection.OptionalArchitecturalPlanChanges,
    optionsGiven: data.optionsGiven ?? contractSection.OptionsGiven,
    optionsRetailValue: data.optionsRetailValue ?? contractSection.OptionsRetailValue,
    planElevationNumber: data.planElevationNumber ?? data.elevation ?? genInfo.PlanElevationNumber,
    planNumber: data.planNumber ?? data.plan ?? genInfo.PlanNumber,
    preparedBy: account.name as string,
    profitAnalysisId: data.profitAnalysisId ?? genInfo.ProfitAnalysisId,
    jobType: data.jobType ?? data.spec_Build ?? genInfo.JobType,
    projectId: data.projectId ?? genInfo.ProjectId,
    projectNumber: data.projectNumber ?? genInfo.ProjectNumber,
    projectedCloseDate: data.projectedCloseDate,
    realtorCommissions: data.realtorCommissions ?? softCostSection.RealtorCommissions,
    realtorCommissionsadjusted: data.realtorCommissionsadjusted ?? softCostSection.RealtorCommissionsadjusted,
    revision: 0,
    salesAllowanceClosingCosts: data.salesAllowanceClosingCosts ?? contractSection.SalesAllowanceClosingCosts,
    salesAllowanceOptions: data.salesAllowanceOptions ?? contractSection.SalesAllowanceOptions,
    salesAllowancePriceReduced: data.salesAllowancePriceReduced ?? contractSection.SalesAllowancePriceReduced,
    salesPrice: data.salesPrice ?? genInfo.SalesPrice,
    specialMarketingIncentive: data.specialMarketingIncentive ?? softCostSection.SpecialMarketingIncentive,
    stageCode: data.stageCode ?? genInfo.StageCode,
    streetAddress: data.streetAddress ?? genInfo.StreetAddress,
    subdivision: data.subdivision ?? genInfo.Subdivision,
    specialLabor: data.specialLabor ?? laborSection.SpecialLabor,
    specialMaterial: data.specialMaterial ?? materialSection.SpecialMaterial,
  });

  // ── getDetails (edit / view) ─────────────────────────────────────────────────
  const getDetails = (detailId: string) => {
    profitAnalysisApi
      .getPADetails(detailId)
      .then((res) => {
        if (!res.data || !res.data.currentValues || !res.data.initialValues || !res.data.changeCount) {
          console.error("Invalid response data:", res.data);
          alert("No data exists for this Profit Analysis. Redirecting to home page.");
          window.location.href = "/";
          return;
        }

        const profileAnalysisDetails: ProfitAnalysisDetails = res.data;
        const data = profileAnalysisDetails.currentValues;
        const initialData = profileAnalysisDetails.initialValues;
        const changeCount = profileAnalysisDetails.changeCount;

        setProjectedCloseDateChanges(profileAnalysisDetails.projectedCloseDateChanges);
        setExitStrategyChanges(profileAnalysisDetails.exitStrategyChanges);

        setLotPremium(data.lotPremium.toString());
        setMarketAdjustment(data.specialMarketingIncentive.toString());

        setGenInfo({
          Subdivision: data.subdivision,
          StreetAddress: data.streetAddress,
          JobId: data.jobId,
          ProjectId: data.projectId,
          ProjectNumber: data.projectNumber,
          JobNumber: data.jobNumber,
          Buyer: data.buyer,
          PlanElevationNumber: data.elevation,
          PlanNumber: data.plan,
          JobType: data.spec_Build,
          ExitStrategy: data.exitStrategy,
          DateSold: moment(data.dateSold) as any,
          ProjectedCloseDate: moment(data.projectedCloseDate).startOf("day") as any,
          CurrentUser: account.name as string,
          SalesPrice: data.salesPrice,
          CloseDate: data.closeDate ? (moment(data.closeDate) as any) : null,
          ProfitAnalysisId: data.profitAnalysisId,
          StageCode: data.stageCode,
          BPPercent: data.bpPercent,
          BLPercent: data.blPercent,
          DiscountToBP: data.discountToBP,
          DiscountToBL: data.discountToBL,
          SalesPriceAtBP: data.salesPriceAtBP,
          SalesPriceAtBL: data.salesPriceAtBL,
        });

        const loadedContract: ContractSectionProps = {
          BrochurePrice: data.brochurePrice,
          LotPremium: data.lotPremium,
          OptionalArchitecturalPlanChanges: data.optionalArchitecturalPlanChanges,
          CustomArchitecturalPlanChanges: data.customArchitecturalPlanChanges,
          BuyerSelectedOptions: data.buyerSelectedOptions,
          OptionsGiven: data.optionsGiven,
          OptionsRetailValue: data.optionsRetailValue,
          CashOptionsPaid: data.cashOptionsPaid,
          SalesAllowanceOptions: Math.abs(data.salesAllowanceOptions),
          SalesAllowanceClosingCosts: Math.abs(data.salesAllowanceClosingCosts),
          SalesAllowancePriceReduced: Math.abs(data.salesAllowancePriceReduced),
        };
        setContractSection(loadedContract);

        const loadedLabor = { LaborBudget: data.laborBudget, ExtraLaborActual: data.extraLaborActual, SpecialLabor: data.specialLabor };
        setLaborSection(loadedLabor);

        const loadedMaterial = { MaterialBudget: data.materialBudget, ExtraMaterialBudget: data.extraMaterialBudget, SpecialMaterial: data.specialMaterial };
        setMaterialSection(loadedMaterial);

        const loadedLotExtra = { LotExtras: data.lotExtras };
        setLotExtraSection(loadedLotExtra);

        const loadedOptions = { CostOfOptionsWithContract: data.costOfOptionsWithContract };
        setOptionsSection(loadedOptions);

        const loadedSoftCost: SoftCostSectionProps = {
          LotCost: data.lotCost,
          Discretionary: data.discretionary,
          HasRealtor: data.hasRealtor,
          RealtorCommissions: data.realtorCommissions,
          SpecialMarketingIncentive: data.specialMarketingIncentive,
          RealtorCommissionsadjusted: Math.abs(data.realtorCommissionsadjusted),
          InHouseCommissions: data.inHouseCommissions,
          InterimInterestBudget: data.interimInterestBudget,
          ClosingCostBudget: data.closingCostBudget,
          BuilderBonus: data.builderBonus,
        };
        setSoftCostSection(loadedSoftCost);

        setNotesList(data.notes || []);

        // initial / change-count mirrors
        setInitialContractSection({
          BrochurePrice: initialData.brochurePrice, LotPremium: initialData.lotPremium,
          OptionalArchitecturalPlanChanges: initialData.optionalArchitecturalPlanChanges,
          CustomArchitecturalPlanChanges: initialData.customArchitecturalPlanChanges,
          BuyerSelectedOptions: initialData.buyerSelectedOptions, OptionsGiven: initialData.optionsGiven,
          OptionsRetailValue: initialData.optionsRetailValue, CashOptionsPaid: initialData.cashOptionsPaid,
          SalesAllowanceOptions: Math.abs(initialData.salesAllowanceOptions),
          SalesAllowanceClosingCosts: Math.abs(initialData.salesAllowanceClosingCosts),
          SalesAllowancePriceReduced: Math.abs(initialData.salesAllowancePriceReduced),
        });
        setChangeCountContractSection({
          BrochurePrice: changeCount.brochurePrice, LotPremium: changeCount.lotPremium,
          OptionalArchitecturalPlanChanges: changeCount.optionalArchitecturalPlanChanges,
          CustomArchitecturalPlanChanges: changeCount.customArchitecturalPlanChanges,
          BuyerSelectedOptions: changeCount.buyerSelectedOptions, OptionsGiven: changeCount.optionsGiven,
          OptionsRetailValue: changeCount.optionsRetailValue, CashOptionsPaid: changeCount.cashOptionsPaid,
          SalesAllowanceOptions: changeCount.salesAllowanceOptions,
          SalesAllowanceClosingCosts: changeCount.salesAllowanceClosingCosts,
          SalesAllowancePriceReduced: changeCount.salesAllowancePriceReduced,
        });
        setInitialLaborSection({ LaborBudget: initialData.laborBudget, ExtraLaborActual: initialData.extraLaborActual, SpecialLabor: initialData.specialLabor });
        setChangeCountLaborSection({ LaborBudget: changeCount.laborBudget, ExtraLaborActual: changeCount.extraLaborActual, SpecialLabor: changeCount.specialLabor });
        setInitialMaterialSection({ MaterialBudget: initialData.materialBudget, ExtraMaterialBudget: initialData.extraMaterialBudget, SpecialMaterial: initialData.specialMaterial });
        setChangeCountMaterialSection({ MaterialBudget: changeCount.materialBudget, ExtraMaterialBudget: changeCount.extraMaterialBudget, SpecialMaterial: changeCount.specialMaterial });
        setInitialLotExtraSection({ LotExtras: initialData.lotExtras });
        setChangeCountLotExtraSection({ LotExtras: changeCount.lotExtras });
        setInitialOptionsSection({ CostOfOptionsWithContract: initialData.costOfOptionsWithContract });
        setChangeCountOptionsSection({ CostOfOptionsWithContract: changeCount.costOfOptionsWithContract });
        setInitialSoftCostSection({
          LotCost: initialData.lotCost, Discretionary: initialData.discretionary, HasRealtor: initialData.hasRealtor,
          RealtorCommissions: initialData.realtorCommissions, SpecialMarketingIncentive: initialData.specialMarketingIncentive,
          RealtorCommissionsadjusted: initialData.realtorCommissionsadjusted, InHouseCommissions: initialData.inHouseCommissions,
          InterimInterestBudget: initialData.interimInterestBudget, ClosingCostBudget: initialData.closingCostBudget, BuilderBonus: initialData.builderBonus,
        });
        setChangeCountSoftCostSection({
          LotCost: changeCount.lotCost, Discretionary: changeCount.discretionary, HasRealtor: changeCount.hasRealtor,
          RealtorCommissions: changeCount.realtorCommissions, SpecialMarketingIncentive: changeCount.specialMarketingIncentive,
          RealtorCommissionsadjusted: changeCount.realtorCommissionsadjusted, InHouseCommissions: changeCount.inHouseCommissions,
          InterimInterestBudget: changeCount.interimInterestBudget, ClosingCostBudget: changeCount.closingCostBudget, BuilderBonus: changeCount.builderBonus,
        });

        if (paType === "sold") {
          const loadedFwd: ForwardCommitmentSectionProps = {
            HasForwardCommitment: data.hasForwardCommitment,
            ForwardCommitmentExpiryDate:
              data.forwardCommitmentExpiryDate === null || data.forwardCommitmentExpiryDate === "0001-01-01T00:00:00"
                ? undefined
                : moment(data.forwardCommitmentExpiryDate).startOf("day"),
            ForwardCommitmentBatchNumber: data.forwardCommitmentBatchNumber,
          };
          setForwardCommitmentSection(loadedFwd);
          setInitialForwardCommitmentSection({
            HasForwardCommitment: initialData.hasForwardCommitment,
            ForwardCommitmentExpiryDate: moment(initialData.forwardCommitmentExpiryDate).startOf("day"),
            ForwardCommitmentBatchNumber: initialData.forwardCommitmentBatchNumber,
          });
          setChangeCountForwardCommitmentSection({
            HasForwardCommitment: changeCount.hasForwardCommitment,
            ForwardCommitmentExpiryDate: changeCount.forwardCommitmentExpiryDate,
            ForwardCommitmentBatchNumber: changeCount.forwardCommitmentBatchNumber,
          });
        }

        setPdfData(buildPdfData({
          ...data,
          planElevationNumber: data.elevation,
          planNumber: data.plan,
          jobType: data.spec_Build,
          dateSold: moment(data.dateSold) as any,
          projectedCloseDate: moment(data.projectedCloseDate).startOf("day") as any,
        }));

        // Store initial form state for unsaved-changes detection (edit mode)
        if (mode === "edit") {
          const baseInitial: SpecFormState = {
            genInfo: {
              Subdivision: data.subdivision, StreetAddress: data.streetAddress, JobId: data.jobId,
              ProjectId: data.projectId, ProjectNumber: data.projectNumber, JobNumber: data.jobNumber,
              Buyer: data.buyer, PlanElevationNumber: data.elevation, PlanNumber: data.plan,
              JobType: data.spec_Build, ExitStrategy: data.exitStrategy,
              DateSold: moment(data.dateSold) as any,
              ProjectedCloseDate: moment(data.projectedCloseDate).startOf("day"),
              CurrentUser: account.name as string, SalesPrice: data.salesPrice,
              CloseDate: data.closeDate ? moment(data.closeDate) : null,
              ProfitAnalysisId: data.profitAnalysisId, StageCode: data.stageCode,
              BPPercent: data.bpPercent, BLPercent: data.blPercent,
              DiscountToBP: data.discountToBP, DiscountToBL: data.discountToBL,
              SalesPriceAtBP: data.salesPriceAtBP, SalesPriceAtBL: data.salesPriceAtBL,
            },
            contractSection: loadedContract,
            laborSection: loadedLabor,
            materialSection: loadedMaterial,
            lotExtraSection: loadedLotExtra,
            optionsSection: loadedOptions,
            softCostSection: loadedSoftCost,
            notesList: data.notes || [],
          };
          if (paType === "sold") {
            setInitialFormState({
              ...baseInitial,
              forwardCommitmentSection: {
                HasForwardCommitment: data.hasForwardCommitment,
                ForwardCommitmentExpiryDate: data.forwardCommitmentExpiryDate === null || data.forwardCommitmentExpiryDate === "0001-01-01T00:00:00"
                  ? undefined
                  : moment(data.forwardCommitmentExpiryDate).startOf("day"),
                ForwardCommitmentBatchNumber: data.forwardCommitmentBatchNumber,
              },
            } as SoldFormState);
          } else {
            setInitialFormState(baseInitial);
          }
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  React.useEffect(() => {
    if ((mode === "edit" || mode === "view") && id) {
      getDetails(id);
    }
  }, [id]);

  // ── create: job selection effects ───────────────────────────────────────────
  React.useEffect(() => {
    if (mode !== "create") return;
    if (selectedJob && genInfo.Subdivision && genInfo.Subdivision.length && selectedJob.subdivision !== genInfo.Subdivision) {
      setSelectedJob(undefined);
    }
  }, [selectedJob, genInfo]);

  React.useEffect(() => {
    if (mode !== "create") return;
    if (selectedJob) {
      setGenInfo((prev) => ({
        ...prev,
        Subdivision: selectedJob.subdivision,
        StreetAddress: selectedJob.jobAddress,
        JobId: selectedJob.jobId,
        ProjectId: selectedJob.projectId,
        JobNumber: selectedJob.jobNumber,
        ProjectNumber: selectedJob.projectNumber,
        Buyer: selectedJob.buyerCombined,
        PlanElevationNumber: selectedJob.planElevationNumber,
        PlanNumber: selectedJob.planNumber,
        JobType: selectedJob.jobType,
        DateSold: (selectedJob && selectedJob.soldDate) ? (moment(selectedJob.soldDate) as any) : null,
        SalesPrice: selectedJob.salesPrice,
        CloseDate: moment("0001-01-01T00:00:00") as any,
        StageCode: selectedJob.stageCode,
        BPPercent: selectedJob.bpPercent,
        BLPercent: selectedJob.blPercent,
        DiscountToBL: selectedJob.discountToBL,
        DiscountToBP: selectedJob.discountToBP,
        SalesPriceAtBL: selectedJob.salesPriceAtBL,
        SalesPriceAtBP: selectedJob.salesPriceAtBP,
      }));
    } else {
      setGenInfo((prev) => ({
        ...prev,
        Subdivision: locationStateSubdivision || null,
        StreetAddress: locationStateJobAddress || "",
        JobId: null,
        ProjectId: null,
        JobNumber: "",
        ProjectNumber: "",
        Buyer: "",
        PlanElevationNumber: "",
        PlanNumber: "",
        JobType: "",
        DateSold: null,
        SalesPrice: null,
        CloseDate: moment("0001-01-01T00:00:00") as any,
        ExitStrategy: undefined,
        StageCode: "",
        BPPercent: undefined,
        BLPercent: undefined,
        DiscountToBL: undefined,
        DiscountToBP: undefined,
        SalesPriceAtBL: undefined,
        SalesPriceAtBP: undefined,
      }));
      setExitStrategyError(false);
      setFormReset(true);
    }
  }, [selectedJob]);

  React.useEffect(() => {
    if (mode !== "create") return;
    if (location.state) {
      setSelectedJob(jobs.find((job) => job.jobId === location.state.jobId));
    } else if (locationStateJobAddress && jobs.length > 0 && !selectedJob) {
      const jobFromLocation = jobs.find(
        (job) =>
          job.subdivision === (locationStateSubdivision || genInfo.Subdivision) &&
          job.jobAddress === locationStateJobAddress
      );
      if (jobFromLocation) setSelectedJob(jobFromLocation);
    }
  }, [location.state, jobs]);

  React.useEffect(() => {
    if (mode !== "create") return;
    const close = document.getElementsByClassName("MuiAutocomplete-clearIndicator")[0];
    if (close) {
      close.addEventListener("click", () => { setSelectedJob(undefined); });
    }
  }, [genInfo.Subdivision, isReviewing]);

  // ── create: form reset effect ────────────────────────────────────────────────
  React.useEffect(() => {
    if (mode !== "create" || !formReset) return;
    setIsReviewing(false);
    location.state = null;
    setGenInfo((prev) => ({
      ...prev,
      Subdivision: locationStateSubdivision || null,
      StreetAddress: locationStateJobAddress || "",
      JobId: null, ProjectId: null, JobNumber: "", ProjectNumber: "",
      Buyer: "", PlanElevationNumber: "", PlanNumber: "", JobType: "",
      ExitStrategy: undefined, DateSold: null, ProjectedCloseDate: null,
      CurrentUser: account.name as string, SalesPrice: null, StageCode: "",
    }));
    setContractSection({
      BrochurePrice: undefined, LotPremium: undefined, OptionalArchitecturalPlanChanges: undefined,
      CustomArchitecturalPlanChanges: undefined, BuyerSelectedOptions: undefined, OptionsGiven: undefined,
      OptionsRetailValue: undefined, CashOptionsPaid: undefined, SalesAllowanceOptions: undefined,
      SalesAllowanceClosingCosts: undefined, SalesAllowancePriceReduced: undefined,
    });
    setLaborSection({ LaborBudget: undefined, ExtraLaborActual: undefined, SpecialLabor: undefined });
    setMaterialSection({ MaterialBudget: undefined, ExtraMaterialBudget: undefined, SpecialMaterial: undefined });
    setLotExtraSection({ LotExtras: undefined });
    setOptionsSection({ CostOfOptionsWithContract: undefined });
    setSoftCostSection({
      LotCost: 0, Discretionary: 0, HasRealtor: true,
      RealtorCommissions: 0, SpecialMarketingIncentive: 0,
      RealtorCommissionsadjusted: 0, InHouseCommissions: 0,
      InterimInterestBudget: 0, ClosingCostBudget: 0, BuilderBonus: 0,
    });
    if (paType === "sold") {
      setForwardCommitmentSection({
        HasForwardCommitment: false,
        ForwardCommitmentExpiryDate: undefined,
        ForwardCommitmentBatchNumber: undefined,
      });
    }
    setSelectedJob(undefined);
    setExitStrategyError(false);
    setNotesList([]);
    setNote("");
    setFormReset(false);
  }, [formReset]);

  // ── running totals ───────────────────────────────────────────────────────────
  const runningTotals = useRunningTotals({
    contractSection,
    laborSection,
    materialSection,
    lotExtraSection,
    optionsSection,
    softCostSection,
    exitStrategy: genInfo.ExitStrategy,
    paType,
    bpPercent: paType === "spec" ? genInfo.BPPercent : null,
    blPercent: paType === "spec" ? genInfo.BLPercent : null,
  });

  const exitStrategy = genInfo.ExitStrategy === undefined ? 0 : genInfo.ExitStrategy;
  const subtotalLaborMaterial = (runningTotals.labor || 0) + (runningTotals.material || 0) + (runningTotals.lotExtras || 0);
  const totalLaborMaterial = (subtotalLaborMaterial || 0) + (runningTotals.options || 0);
  const totalCost = (runningTotals.laborMaterial || 0) + (runningTotals.softCost || 0);

  const ableToCreate = validateState(genInfo);

  // ── create: subdivision helpers ──────────────────────────────────────────────
  const subdivisions = mode === "create"
    ? ([...new Set(jobs.filter((j) => !j.closeDate && j.profitAnalysisId === 0 && j.role === "Write").map((j) => j.subdivision))] as string[])
    : [];

  const filterOptions = createFilterOptions<JobProps>({
    matchFrom: "any", trim: true, limit: 100, ignoreAccents: true, ignoreCase: true,
    stringify: (option) => option.jobAddress,
  });

  const handleSubdivisionChange = (_event: any, newValue: string | null) => {
    setGenInfo((prev) => ({ ...prev, Subdivision: newValue as string }));
    if (!newValue) {
      setGenInfo((prev) => ({
        ...prev,
        StreetAddress: locationStateJobAddress || "",
        JobId: null, ProjectId: null, JobNumber: "", ProjectNumber: "",
        Buyer: "", PlanElevationNumber: "", PlanNumber: "", JobType: "",
        DateSold: null, SalesPrice: null, StageCode: "",
      }));
      setSelectedJob(undefined);
    }
  };

  // ── API: build common payload ────────────────────────────────────────────────
  const buildPayload = () => ({
    ...genInfo,
    ...contractSection,
    SalesAllowanceOptions: contractSection.SalesAllowanceOptions ? contractSection.SalesAllowanceOptions : 0,
    SalesAllowanceClosingCosts: contractSection.SalesAllowanceClosingCosts ? contractSection.SalesAllowanceClosingCosts : 0,
    SalesAllowancePriceReduced: contractSection.SalesAllowancePriceReduced ? contractSection.SalesAllowancePriceReduced : 0,
    ...laborSection,
    ...materialSection,
    ...lotExtraSection,
    ...optionsSection,
    ...softCostSection,
    ...(paType === "sold" ? forwardCommitmentSection : {}),
    RealtorCommissionsadjusted: softCostSection.RealtorCommissionsadjusted ? softCostSection.RealtorCommissionsadjusted : 0,
    SalesPriceAtBP: runningTotals.bpPrice,
    SalesPriceAtBL: runningTotals.blPrice,
  });

  // ── handleCreate ─────────────────────────────────────────────────────────────
  const handleCreate = (createAnother: boolean) => {
    if (
      (!genInfo.ExitStrategy && genInfo.ExitStrategy !== 0) ||
      !genInfo.ProjectedCloseDate ||
      !contractSection.BrochurePrice
    ) {
      alert("Exit Strategy, Projected Closed Date, and Brochure price are required");
      return;
    }
    if (genInfo.ExitStrategy && genInfo.ExitStrategy < -100) { alert("Exit strategy cannot be less than -100%"); return; }
    if (genInfo.ExitStrategy && genInfo.ExitStrategy > 100) { alert("Exit strategy cannot be greater than 100%"); return; }

    if (paType === "sold" && forwardCommitmentSection.HasForwardCommitment && (!forwardCommitmentSection.ForwardCommitmentExpiryDate || !forwardCommitmentSection.ForwardCommitmentBatchNumber)) {
      alert("When Forward Commitment is used, Expiry Date and Batch Number are required");
      return;
    }

    if (!softCostSection.HasRealtor) setSoftCostSection((prev) => ({ ...prev, RealtorCommissions: 0 }));
    if (paType === "sold" && !forwardCommitmentSection.HasForwardCommitment) {
      setForwardCommitmentSection((prev) => ({ ...prev, ForwardCommitmentExpiryDate: undefined, ForwardCommitmentBatchNumber: undefined }));
    }

    profitAnalysisApi
      .createPA(buildPayload())
      .then((res) => {
        if (res.status === 201 || res.status === 200) {
          const profitAnalysisId = res?.data ?? 0;
          const allNotes = [...notesList];
          if (String(note).trim()) {
            allNotes.push({
              profitAnalysisNoteId: 0,
              jobId: genInfo.JobId,
              profitAnalysisId,
              note: String(note).trim(),
              createdBy: account.name as string,
              createdOn: moment().format(),
            });
          }
          Promise.all(
            allNotes.map((n) => profitAnalysisApi.saveNote({ ProfitAnalysisId: profitAnalysisId, JobId: genInfo.JobId, Note: n.note }))
          )
            .catch((err) => { console.error("Error saving notes:", err); })
            .finally(() => {
              setNotesList([]);
              setNote("");
              if (!createAnother) {
                navigate("/");
              } else {
                setFormReset(true);
              }
              alert("Successfully created your profit analysis.");
            });
        } else {
          if (res?.data?.Message) alert(res.data.Message);
          else alert("There was an error in creating your profit analysis");
        }
      })
      .catch((err) => {
        console.error(err);
        if (err?.Message) alert(err.Message);
        else alert("There was an error creating your profit analysis");
      });
  };

  // ── handleSave (edit / view) ─────────────────────────────────────────────────
  const handleSave = (createAnother: boolean) => {
    if (mode === "edit" && genInfo.CloseDate !== null) {
      dispatch({ type: "Snackbar", payload: { show: true, message: "Cannot edit/save an already Closed Job", severity: "warning" } });
      return;
    }
    if (
      (!genInfo.ExitStrategy && genInfo.ExitStrategy !== 0) ||
      !genInfo.ProjectedCloseDate ||
      !contractSection.BrochurePrice
    ) {
      alert("Exit Strategy, Projected Closed Date, and Brochure price are required");
      return;
    }
    if (genInfo.ExitStrategy && genInfo.ExitStrategy < -100) { alert("Exit strategy cannot be less than -100%"); return; }
    if (genInfo.ExitStrategy && genInfo.ExitStrategy > 100) { alert("Exit strategy cannot be greater than 100%"); return; }

    if (!softCostSection.HasRealtor) setSoftCostSection((prev) => ({ ...prev, RealtorCommissions: 0 }));

    profitAnalysisApi
      .updatePA(id as string, buildPayload())
      .then((res) => {
        if (res.status === 200 && res.data === "") {
          setIsReviewing(false);

          if (mode === "edit") {
            dispatch({ type: "Snackbar", payload: { show: true, message: "Successfully saved updates to your profit analysis.", severity: "success" } });

            const updatedPdfData = buildPdfData({
              ...genInfo,
              ...contractSection,
              ...laborSection,
              ...materialSection,
              ...lotExtraSection,
              ...optionsSection,
              ...softCostSection,
              planElevationNumber: genInfo.PlanElevationNumber,
              planNumber: genInfo.PlanNumber,
              jobType: genInfo.JobType,
              dateSold: genInfo.DateSold,
              projectedCloseDate: moment(genInfo.ProjectedCloseDate).format("MM-DD-YYYY HH:mm A"),
            });
            setPdfData(updatedPdfData);

            const baseUpdated: SpecFormState = {
              genInfo, contractSection, laborSection, materialSection,
              lotExtraSection, optionsSection, softCostSection, notesList,
            };
            setInitialFormState(paType === "sold"
              ? { ...baseUpdated, forwardCommitmentSection } as SoldFormState
              : baseUpdated
            );

            if (projectedCloseDateFieldUpdated && genInfo.ProjectedCloseDate.format("YYYY-MM-DD") !== moment(projectedCloseDateChanges[0]?.projectedCloseDate).format("YYYY-MM-DD")) {
              setProjectedCloseDateChanges([{
                projectedCloseDate: moment(genInfo.ProjectedCloseDate).format("MM-DD-YYYY"),
                updatedOn: moment().format("MM-DD-YYYY HH:mm:ss"),
                updatedBy: genInfo.CurrentUser,
                versionId: -1,
                projectedCloseDateChanges: "",
              }, ...projectedCloseDateChanges]);
              setProjectedCloseDateFieldUpdated(false);
            }

            if (exitStrategyFieldUpdated && genInfo.ExitStrategy !== exitStrategyChanges[0]?.exitStrategy) {
              setExitStrategyChanges([{
                exitStrategy: genInfo.ExitStrategy,
                updatedOn: moment().format("MM-DD-YYYY HH:mm:ss"),
                updatedBy: genInfo.CurrentUser,
                versionId: -1,
                exitStrategyChanges: "",
              }, ...exitStrategyChanges]);
              setExitStrategyFieldUpdated(false);
            }
          } else {
            alert("Successfully saved updates to your profit analysis.");
          }
        } else {
          const msg = res?.data?.Message;
          if (mode === "edit") {
            dispatch({ type: "Snackbar", payload: { show: true, message: msg || "There was an error saving your profit analysis", severity: msg ? "success" : "error" } });
          } else {
            alert("There was an error creating your profit analysis");
          }
        }
      })
      .catch((err) => {
        console.error(err);
        if (mode === "edit") {
          dispatch({ type: "Snackbar", payload: { show: true, message: err?.Message || "There was an error saving your profit analysis", severity: "error" } });
        } else {
          alert("There was an error creating your profit analysis.");
        }
      });
  };

  // ── handleSaveNotes (edit / view) ────────────────────────────────────────────
  const handleSaveNotes = () => {
    if (!String(note).trim()) return;
    setNotesList([{
      profitAnalysisNoteId: 0,
      jobId: genInfo.JobId,
      profitAnalysisId: genInfo.ProfitAnalysisId,
      note: String(note).trim(),
      createdBy: account.name as string,
      createdOn: moment().format(),
    }, ...notesList]);

    profitAnalysisApi
      .saveNote({ ProfitAnalysisId: parseInt(id as string), JobId: genInfo.JobId, Note: note })
      .then((res) => {
        if (res.status === 201) {
          setNote("");
          dispatch({ type: "Snackbar", payload: { show: true, message: "Notes saved successfully.", severity: "success" } });
          if (initialFormState) {
            setInitialFormState({ ...initialFormState, notesList: [...initialFormState.notesList] });
          }
        } else {
          dispatch({ type: "Snackbar", payload: { show: true, message: "There was an error saving your notes", severity: "error" } });
        }
      })
      .catch((err) => {
        console.error(err);
        dispatch({ type: "Snackbar", payload: { show: true, message: "There was an error saving your notes", severity: "error" } });
      });
  };

  const handleSubmitConfirm = () => { handleSave(false); setOpenConfirmation(false); };
  const handleCancelConfirmation = () => { setIsReviewing(false); setOpenConfirmation(false); };

  // ── section mode prop: sections receive "edit" or "review" ──────────────────
  // view page: all fields are hardcoded disabled, so we always pass "edit" to sections
  // (sections only disable on "review"; view disables via hardcoded disabled props)
  const sectionMode: "edit" | "review" = isReviewing ? "review" : "edit";
  const pageVariant: "create" | "edit-view" = mode === "create" ? "create" : "edit-view";

  // ── mobile shell support ─────────────────────────────────────────────────────
  // Maps field names to their section ids so the error banner knows where to jump.
  const fieldToSectionId: Record<string, string> = {
    BrochurePrice: "contract",
    LotPremium: "contract",
    OptionalArchitecturalPlanChanges: "contract",
    CustomArchitecturalPlanChanges: "contract",
    BuyerSelectedOptions: "contract",
    OptionsGiven: "contract",
    OptionsRetailValue: "contract",
    CashOptionsPaid: "contract",
    SalesAllowanceOptions: "contract",
    SalesAllowanceClosingCosts: "contract",
    SalesAllowancePriceReduced: "contract",
    LaborBudget: "labor",
    ExtraLaborActual: "labor",
    SpecialLabor: "labor",
    MaterialBudget: "material",
    ExtraMaterialBudget: "material",
    SpecialMaterial: "material",
    LotExtras: "lotExtras",
    CostOfOptionsWithContract: "options",
    LotCost: "softCost",
    Discretionary: "softCost",
    RealtorCommissions: "softCost",
    SpecialMarketingIncentive: "softCost",
    RealtorCommissionsadjusted: "softCost",
    InHouseCommissions: "softCost",
    InterimInterestBudget: "softCost",
    ClosingCostBudget: "softCost",
    BuilderBonus: "softCost",
  };

  const erroringFields = Object.entries(formErrors).filter(([, v]) => v !== "");
  const fieldErrorCount = erroringFields.length;
  const percentErrorCount =
    (exitStrategyError ? 1 : 0) +
    (paType === "spec" ? (bpPercentError ? 1 : 0) + (blPercentError ? 1 : 0) : 0);
  const errorCount = fieldErrorCount + percentErrorCount;
  const firstErrorSectionId: string | undefined =
    percentErrorCount > 0
      ? "jobInfo"
      : erroringFields.length > 0
        ? fieldToSectionId[erroringFields[0][0]]
        : undefined;

  // Money formatter used by both desktop KpiSidebar and mobile KPI tab.
  const formatCurrency = (v: number) =>
    `$${numberWithCommas(mode === "create" ? v : Math.round(v))}`;

  // Print handler that works for all three modes. Create mode builds pdf data
  // from live state via buildPdfData({}); edit/view use the saved snapshot.
  const handlePrint = () => {
    if (
      (!genInfo.ExitStrategy && genInfo.ExitStrategy !== 0) ||
      !genInfo.ProjectedCloseDate ||
      !contractSection.BrochurePrice
    ) {
      alert("Exit Strategy, Projected Closed Date, and Brochure price are required");
      return;
    }
    if (genInfo.ExitStrategy && genInfo.ExitStrategy < -100) {
      alert("Exit strategy cannot be less than -100%");
      return;
    }
    if (genInfo.ExitStrategy && genInfo.ExitStrategy > 100) {
      alert("Exit strategy cannot be greater than 100%");
      return;
    }
    const livePdfData: pdfProps = pdfData ?? buildPdfData({});
    if (mode === "edit" && hasUnsavedChanges) {
      setShowPrintWarning(true);
      return;
    }
    generatePDF(
      livePdfData,
      genInfo,
      contractSection,
      paType,
      laborSection,
      materialSection,
      lotExtraSection,
      optionsSection,
      softCostSection,
      paType === "sold" ? forwardCommitmentSection : null,
      runningTotals,
      notesList
    );
  };

  // Print is gated by the same required-fields rule on all modes.
  const printDisabled =
    contractSection.BrochurePrice == null ||
    genInfo.ExitStrategy == null ||
    !genInfo.ProjectedCloseDate;

  const handlePrintChoiceConfirm = () => {
    setShowPrintWarning(false);
    if (printChoice === "saveAndPrint") {
      handleSave(false); // current behavior: save, then print
    }
    generatePDF(
      printChoice === "printOnly"
        ? buildPdfData({ dateSold: genInfo.DateSold, projectedCloseDate: genInfo.ProjectedCloseDate })
        : (pdfData ?? buildPdfData({})),
      genInfo,
      contractSection,
      paType,
      laborSection,
      materialSection,
      lotExtraSection,
      optionsSection,
      softCostSection,
      paType === "sold" ? forwardCommitmentSection : null,
      runningTotals,
      notesList
    );
  };

  // Shared by the mobile and desktop branches.
  const printUnsavedDialog = (
    <Dialog open={showPrintWarning} onClose={() => setShowPrintWarning(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Unsaved Changes</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>There is unsaved data.</DialogContentText>
        <RadioGroup
          value={printChoice}
          onChange={(e) => setPrintChoice(e.target.value as "saveAndPrint" | "printOnly")}
        >
          <FormControlLabel value="saveAndPrint" control={<Radio />} label="Do you want to save and print?" />
          <FormControlLabel value="printOnly" control={<Radio />} label="Print without saving" />
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowPrintWarning(false)}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={handlePrintChoiceConfirm}>
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── render ───────────────────────────────────────────────────────────────────
  if (!isDesktop) {
    // Build the section picker list. Each item maps to a `renderSection(id)`
    // case below.  Job Info and Notes get their own entries so the picker is
    // the single navigation surface on mobile.
    const fmt = (v: number | undefined) =>
      typeof v === "number" && v !== 0 ? formatCurrency(v) : undefined;

    const sectionItems: MobileSectionPickerItem[] = [
      ...(mode === "edit" || mode === "view"
        ? [
          {
            id: "changeHistory",
            label: "Change History",
            icon: <ChangeHistoryIcon />,
          },
        ]
        : []),
      { id: "jobInfo", label: "Job Info", icon: <Icon>info</Icon> },
      {
        id: "contract",
        label: "Contract",
        icon: <TextSnippetIcon />,
        trailing: fmt(runningTotals.contract),
      },
      {
        id: "labor",
        label: "Labor",
        icon: <ConstructionIcon />,
        trailing: fmt(runningTotals.labor),
      },
      {
        id: "material",
        label: "Material",
        icon: <CabinIcon />,
        trailing: fmt(runningTotals.material),
      },
      {
        id: "lotExtras",
        label: "Lot Extras",
        icon: <LandscapeIcon />,
        trailing: fmt(runningTotals.lotExtras),
      },
      {
        id: "options",
        label: "Options",
        icon: <TuneIcon />,
        trailing: fmt(runningTotals.options),
      },
      {
        id: "softCost",
        label: "Soft Cost",
        icon: <RequestQuoteIcon />,
        trailing: fmt(runningTotals.softCost),
      },
      ...(paType === "sold"
        ? [
          {
            id: "forwardCommitment",
            label: "Forward Commitment",
            icon: <GavelIcon />,
          },
        ]
        : []),
      {
        id: "notes",
        label: "Notes",
        icon: <CommentIcon />,
        trailing: notesList.length > 0 ? `${notesList.length}` : undefined,
      },
    ];

    const sectionTitle = (id: string): string => {
      const found = sectionItems.find((it) => it.id === id);
      return found?.label ?? "Section";
    };

    const sectionTrailing = (id: string): string | undefined => {
      const found = sectionItems.find((it) => it.id === id);
      return found?.trailing;
    };

    // Renders the JSX for one section id, reusing the same section components
    // used by the desktop layout below.  Keeping this inline (vs. a helper
    // module) lets us share all the state/handlers in closure without
    // prop-drilling through MobileShell.
    const renderSection = (id: string): React.ReactNode => {
      switch (id) {
        case "contract":
          return (
            <ContractSection
              state={contractSection}
              setState={setContractSection}
              formErrors={formErrors}
              updateField={updateField}
              total={runningTotals.contract}
              fullRetail={runningTotals.fullRetail}
              mode={sectionMode}
              forceMobile={true}
              pageVariant={mode}
              paType={paType}
              discountToBP={Math.round(runningTotals.discountToBP)}
              bpPrice={Math.round(runningTotals.bpPrice)}
              discountToBL={Math.round(runningTotals.discountToBL)}
              blPrice={Math.round(runningTotals.blPrice)}
              {...(mode !== "create"
                ? {
                  initialState: initialContractSection,
                  changeCount: changeCountContractSection,
                }
                : {})}
            />
          );
        case "labor":
          return (
            <LaborSection
              state={laborSection}
              setState={setLaborSection}
              formErrors={formErrors}
              updateField={updateField}
              total={runningTotals.labor}
              mode={sectionMode}
              forceMobile={true}
              pageVariant={mode}
              {...(mode !== "create"
                ? {
                  initialState: initialLaborSection,
                  changeCount: changeCountLaborSection,
                }
                : {})}
            />
          );
        case "material":
          return (
            <MaterialSection
              state={materialSection}
              setState={setMaterialSection}
              formErrors={formErrors}
              updateField={updateField}
              total={runningTotals.material}
              mode={sectionMode}
              forceMobile={true}
              pageVariant={mode}
              {...(mode !== "create"
                ? {
                  initialState: initialMaterialSection,
                  changeCount: changeCountMaterialSection,
                }
                : {})}
            />
          );
        case "lotExtras":
          return (
            <LotExtrasSection
              state={lotExtraSection}
              setState={setLotExtraSection}
              formErrors={formErrors}
              updateField={updateField}
              total={runningTotals.lotExtras}
              subtotalLaborMaterial={subtotalLaborMaterial}
              mode={sectionMode}
              forceMobile={true}
              pageVariant={mode}
              {...(mode !== "create"
                ? {
                  initialState: initialLotExtraSection,
                  changeCount: changeCountLotExtraSection,
                }
                : {})}
            />
          );
        case "options":
          return (
            <OptionsSection
              state={optionsSection}
              setState={setOptionsSection}
              formErrors={formErrors}
              updateField={updateField}
              total={runningTotals.options}
              totalLaborMaterial={totalLaborMaterial}
              mode={sectionMode}
              forceMobile={true}
              pageVariant={mode}
              {...(mode !== "create"
                ? {
                  initialState: initialOptionsSection,
                  changeCount: changeCountOptionsSection,
                }
                : {})}
            />
          );
        case "softCost":
          return (
            <SoftCostSection
              state={softCostSection}
              setState={setSoftCostSection}
              formErrors={formErrors}
              updateField={updateField}
              total={runningTotals.softCost}
              totalBP={runningTotals.softCostBP}
              totalBL={runningTotals.softCostBL}
              mode={sectionMode}
              forceMobile={true}
              pageVariant={mode}
              marketAdjustment={marketAdjustment}
              paType={paType}
              blPercent={genInfo.BLPercent}
              contractPrice={contractSection.BrochurePrice || 0}
              bpRealtorCommissions={runningTotals.bpRealtorCommissions || 0}
              bpInHouseCommissions={runningTotals.bpInHouseCommissions || 0}
              bpBuilderBonus={runningTotals.bpBuilderBonus || 0}
              blBuilderBonus={runningTotals.blBuilderBonus || 0}
              blInHouseCommissions={runningTotals.blInHouseCommissions || 0}
              blRealtorCommissions={runningTotals.blRealtorCommissions || 0}
              disableCommissions={paType === "spec"}
              {...(mode !== "create"
                ? {
                  initialState: initialSoftCostSection,
                  changeCount: changeCountSoftCostSection,
                }
                : {})}
            />
          );
        case "forwardCommitment":
          return paType === "sold" ? (
            <ForwardCommitmentSection
              state={forwardCommitmentSection}
              setState={setForwardCommitmentSection}
              mode={sectionMode}
              forceMobile={true}
              minDate={minDate}
              pageVariant={mode}
              {...(mode !== "create"
                ? {
                  initialState: initialForwardCommitmentSection,
                  changeCount: changeCountForwardCommitmentSection,
                }
                : {})}
            />
          ) : null;
        case "jobInfo":
          return (
            <Box sx={{ p: 1 }}>
              <Stack spacing={2}>
                {mode === "create" ? (
                  <>
                    <Autocomplete
                      disablePortal
                      options={subdivisions
                        .filter(
                          (sub) =>
                            sub &&
                            jobs.filter(
                              (j) =>
                                j.subdivision === sub &&
                                !j.closeDate &&
                                j.profitAnalysisId === 0 &&
                                j.role === "Write"
                            ).length > 0
                        )
                        .sort()}
                      ref={subdivisionRef}
                      disabled={isReviewing || !!locationStateSubdivision}
                      value={
                        genInfo.Subdivision && subdivisions.includes(genInfo.Subdivision)
                          ? genInfo.Subdivision
                          : genInfo.Subdivision
                            ? genInfo.Subdivision
                            : ""
                      }
                      onChange={handleSubdivisionChange}
                      isOptionEqualToValue={(option, value) => option === value}
                      renderInput={(params) => (
                        <TextField {...params} label="Subdivision*" />
                      )}
                    />
                    <Autocomplete
                      disablePortal
                      key={selectedJob?.jobAddress}
                      disabled={isReviewing || !!locationStateJobAddress}
                      filterOptions={filterOptions}
                      options={jobs.filter(
                        (j) =>
                          j.subdivision === genInfo.Subdivision &&
                          j.profitAnalysisId === 0 &&
                          !j.closeDate &&
                          j.role === "Write"
                      )}
                      getOptionLabel={(option) => option.jobAddress}
                      value={selectedJob}
                      onChange={(_e: any, newValue: JobProps | null) => {
                        setGenInfo((prev) => ({
                          ...prev,
                          StreetAddress: newValue ? newValue.jobAddress : "",
                        }));
                        setSelectedJob(newValue ?? undefined);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Address*"
                          helperText={
                            !genInfo.Subdivision ? "Please select a subdivision first" : ""
                          }
                        />
                      )}
                    />
                  </>
                ) : (
                  <>
                    <TextField
                      label="Subdivision*"
                      value={genInfo.Subdivision ?? ""}
                      variant="filled"
                      disabled
                      fullWidth
                      sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                    />
                    <TextField
                      label="Address*"
                      value={genInfo.StreetAddress ?? ""}
                      variant="filled"
                      disabled
                      fullWidth
                      sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                    />
                  </>
                )}
                <TextField
                  label="Project Number"
                  value={genInfo.ProjectNumber ?? ""}
                  variant="filled"
                  disabled
                  fullWidth
                  sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                />
                <TextField
                  label="Job Number"
                  value={genInfo.JobNumber ?? ""}
                  variant="filled"
                  disabled
                  fullWidth
                  sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                />
                <TextField
                  label="Stage Code"
                  value={genInfo.StageCode ?? ""}
                  variant="filled"
                  disabled
                  fullWidth
                  sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                />
                <TextField
                  label="Buyer"
                  value={genInfo.Buyer ?? ""}
                  variant="filled"
                  disabled
                  fullWidth
                  sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                />
                <TextField
                  label="Plan"
                  value={genInfo.PlanNumber ?? ""}
                  variant="filled"
                  disabled
                  fullWidth
                  sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                />
                <TextField
                  label="Elevation"
                  value={genInfo.PlanElevationNumber ?? ""}
                  variant="filled"
                  disabled
                  fullWidth
                  sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                />
                <TextField
                  label="Job Type"
                  value={genInfo.JobType ?? ""}
                  variant="filled"
                  disabled
                  fullWidth
                  sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                />

                {paType === "sold" && (
                  <TextField
                    label="Date Sold"
                    value={
                      genInfo.DateSold
                        ? moment(genInfo.DateSold).format("MM/DD/YYYY")
                        : ""
                    }
                    variant="filled"
                    disabled
                    fullWidth
                    sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                  />
                )}
                {mode === "view" ? (
                  <SafeDatePicker
                    label="Projected Closed Date*"
                    value={genInfo.ProjectedCloseDate ?? null}
                    onChange={() => { }}
                    minDate={minDate}
                    disabled
                    required
                  />
                ) : (
                  <SafeDatePicker
                    label="Projected Closed Date*"
                    value={genInfo.ProjectedCloseDate ?? null}
                    onChange={(v) => {
                      setGenInfo({ ...genInfo, ProjectedCloseDate: v });
                      if (mode === "edit") setProjectedCloseDateFieldUpdated(true);
                    }}
                    minDate={minDate}
                    disabled={isReviewing}
                    required
                  />
                )}
                {mode === "view" ? (
                  <TextField
                    label="Exit Strategy*"
                    value={genInfo.ExitStrategy ?? ""}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    disabled
                    fullWidth
                  />
                ) : (
                  <PercentField
                    label="Exit Strategy*"
                    value={genInfo.ExitStrategy ?? null}
                    onChange={(v) => {
                      setGenInfo({ ...genInfo, ExitStrategy: v ?? undefined });
                      if (v === null || v === 0 || v < -100 || v > 100) {
                        setExitStrategyError(true);
                      } else {
                        setExitStrategyError(false);
                        if (mode === "edit") setExitStrategyFieldUpdated(true);
                      }
                    }}
                    disabled={isReviewing}
                    required
                    tooltip="Exit Strategy value must be between -100 and 100."
                    error={exitStrategyError}
                    helperText={
                      exitStrategyError
                        ? "Exit Strategy must be within the range of -100 to 100, and not a zero"
                        : ""
                    }
                  />
                )}
                {paType === "spec" && (
                  <>
                    {mode === "view" ? (
                      <TextField
                        label="BP %*"
                        value={genInfo.BPPercent ?? ""}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                        disabled
                        fullWidth
                      />
                    ) : (
                      <PercentField
                        label="BP %*"
                        value={genInfo.BPPercent ?? null}
                        onChange={(v) => {
                          setGenInfo({ ...genInfo, BPPercent: v ?? undefined });
                          setBpPercentError(v === null || v === 0 || v < -100 || v > 100);
                        }}
                        disabled={isReviewing}
                        required
                        tooltip="BP % value must be between -100 and 100."
                        error={bpPercentError}
                        helperText={
                          bpPercentError
                            ? "BP % must be within the range of -100 to 100, and not a zero"
                            : ""
                        }
                      />
                    )}
                    {mode === "view" ? (
                      <TextField
                        label="BL %*"
                        value={genInfo.BLPercent ?? ""}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                        disabled
                        fullWidth
                      />
                    ) : (
                      <PercentField
                        label="BL %*"
                        value={genInfo.BLPercent ?? null}
                        onChange={(v) => {
                          setGenInfo({ ...genInfo, BLPercent: v ?? undefined });
                          setBlPercentError(v === null || v === 0 || v < -100 || v > 100);
                        }}
                        disabled={isReviewing}
                        required
                        tooltip="BL % value must be between -100 and 100."
                        error={blPercentError}
                        helperText={
                          blPercentError
                            ? "BL % must be within the range of -100 to 100, and not a zero"
                            : ""
                        }
                      />
                    )}
                  </>
                )}
              </Stack>
            </Box>
          );
        case "notes":
          return (
            <Box sx={{ p: 1 }}>
              {mode !== "view" && !isReviewing && (
                <Box
                  sx={{
                    backgroundColor: "rgba(183, 176, 255, 0.08)",
                    borderRadius: 2,
                    p: 1.5,
                    mb: 2,
                  }}
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    multiline
                    rows={3}
                    placeholder="Add a note..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                  />
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button
                      variant="contained"
                      disableElevation
                      size="small"
                      disabled={!String(note).trim()}
                      onClick={() => {
                        if (mode === "create") {
                          if (String(note).trim()) {
                            setNotesList([
                              {
                                profitAnalysisId: 0,
                                jobId: genInfo.JobId,
                                profitAnalysisNoteId: 0,
                                note: String(note).trim(),
                                createdBy: account.name as string,
                                createdOn: moment().format(),
                              },
                              ...notesList,
                            ]);
                            setNote("");
                          }
                        } else {
                          handleSaveNotes();
                        }
                      }}
                    >
                      Add note
                    </Button>
                  </Box>
                </Box>
              )}
              {notesList.length > 0 ? (
                notesList.map((n, idx) => (
                  <Box
                    key={mode === "create" ? idx : n.profitAnalysisNoteId}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: grey[50],
                      border: `1px solid ${grey[200]}`,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>
                      {n.createdBy} ·{" "}
                      {moment(n.createdOn).format("MMM D, YYYY [at] h:mm A")}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {n.note}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{ fontStyle: "italic", color: grey[600] }}
                >
                  No notes yet.
                </Typography>
              )}
            </Box>
          );
        case "changeHistory":
          return (
            <Box sx={{ p: 1 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Projected Close Date Changes
              </Typography>
              {projectedCloseDateChanges.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", color: grey[600], mb: 2 }}>
                  No changes recorded.
                </Typography>
              ) : (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {projectedCloseDateChanges.map((change, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${grey[200]}`,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {moment(change.projectedCloseDate).format("MM/DD/YYYY")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight={400}>
                        {moment(change.updatedOn).format(
                          mode === "edit" ? "MM/DD/YYYY HH:mm A" : "MM/DD/YYYY"
                        )}{" "}
                        by {change.updatedBy}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
              <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Exit Strategy Changes
              </Typography>
              {exitStrategyChanges.length === 0 ? (
                <Typography variant="body2" sx={{ fontStyle: "italic", color: grey[600] }}>
                  No changes recorded.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {exitStrategyChanges.map((change, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${grey[200]}`,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {change.exitStrategy}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight={400}>
                        {moment(change.updatedOn).format("MM/DD/YYYY")} by{" "}
                        {change.updatedBy}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          );
        default:
          return null;
      }
    };

    const primaryDisabled = isReviewing
      ? !ableToCreate
      : !checkFormValidity();

    return (
      <div>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <MobileShell
            paType={paType}
            mode={mode}
            isReviewing={isReviewing}
            hasUnsavedChanges={hasUnsavedChanges}
            sectionItems={sectionItems}
            renderSection={renderSection}
            sectionTitle={sectionTitle}
            sectionTrailing={sectionTrailing}
            runningTotals={runningTotals}
            formatCurrency={formatCurrency}
            exitStrategy={exitStrategy}
            totalCost={totalCost}
            varianceNAGuard={mode === "create"}
            errorCount={errorCount}
            firstErrorSectionId={firstErrorSectionId}
            primaryDisabled={primaryDisabled}
            printDisabled={printDisabled}
            onPrint={handlePrint}
            onReview={() => {
              if (ableToCreate) setIsReviewing(true);
              else alert("Please fill out the general information");
            }}
            onExitReview={() => setIsReviewing(false)}
            onSubmit={() => handleCreate(false)}
            onSaveChanges={() => setOpenConfirmation(true)}
            onNavigateToList={() => navigate("/")}
          />
          <ConfirmationDialog
            open={openConfirmation}
            onClose={handleCancelConfirmation}
            onConfirm={handleSubmitConfirm}
            title={mode === "edit" ? "Edit Confirmation" : "Submit Confirmation"}
            content={
              mode === "edit"
                ? "Are you sure you want to confirm these edits?"
                : "Are you sure you want to submit this profit analysis?"
            }
          />
          {printUnsavedDialog}
        </LocalizationProvider>
      </div>
    );
  }

  // ── render (desktop) ────────────────────────────────────────────────────────
  return (
    <div>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Container component="main" maxWidth={false} disableGutters={mode !== "view"}>
          <CssBaseline />
          <Box sx={{ display: "flex" }}>
            <CssBaseline />
            <Header />
            <KpiSidebar
              runningTotals={runningTotals}
              drawerWidth={drawerWidth}
              formatCurrency={(v) => `$${numberWithCommas(mode === "create" ? v : Math.round(v))}`}
              exitStrategy={exitStrategy}
              totalCost={totalCost}
              totalCostBP={runningTotals.totalCostBP}
              totalCostBL={runningTotals.totalCostBL}
              profitUSD={runningTotals.profitUSD}
              profitMargin={runningTotals.profitMargin}
              varianceToExitStrategy={runningTotals.varianceToExitStrategy}
              varianceNAGuard={mode === "create" ? true : undefined}
              stackMt={mode === "create" ? 2 : undefined}
              toolbarColor="red"
              paType={paType}
              bpPrice={runningTotals.bpPrice}
              bpProfit={runningTotals.bpProfit}
              bpPercent={runningTotals.bpPercent}
              blPrice={runningTotals.blPrice}
              blProfit={runningTotals.blProfit}
              blPercent={runningTotals.blPercent}
              exitPrice={runningTotals.exitPrice}
              exitProfit={runningTotals.exitProfit}
              exitPercent={runningTotals.exitPercent}
            />
            <Box component="main" sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 }, pb: { xs: 9, sm: 2, md: 3 } }}>
              {/* ── Action bar: top ─────────────────────────────────────────── */}
              {mode === "create" && isReviewing && (
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2} my={5} sx={{ borderBottom: "0.5px solid", borderColor: "divider", pb: 2 }}>
                  <Box>
                    <Button variant="contained" disableElevation onClick={() => setIsReviewing(false)}>
                      EDIT
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      onClick={() => { handleCreate(false); }}
                      disabled={!ableToCreate}
                      variant="contained"
                      disableElevation
                      sx={{ maxWidth: 300, mr: 2 }}
                    >
                      Submit
                    </Button>
                    <Button
                      onClick={() => { handleCreate(true); }}
                      disabled={!ableToCreate}
                      variant="contained"
                      disableElevation
                      sx={{ maxWidth: 300 }}
                    >
                      Save and create another
                    </Button>
                  </Box>
                </Stack>
              )}

              {mode === "create" && !isReviewing && (
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" spacing={1} my={5} sx={{ borderBottom: "0.5px solid", borderColor: "divider", pb: 2 }}>
                  <Button
                    disableElevation
                    variant="outlined"
                    onClick={handlePrint}
                    disabled={printDisabled}
                    sx={{
                      borderColor: "divider",
                      color: "text.secondary",
                      "&:hover": {
                        borderColor: "text.primary",
                        backgroundColor: "rgba(0,0,0,0.03)"
                      }
                    }}
                  >
                    Print
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      if (ableToCreate) {
                        setIsReviewing(true);
                      } else {
                        alert("Please fill out the general information");
                      }
                    }}
                    sx={{ maxWidth: 300 }}
                    disabled={!checkFormValidity()}
                  >
                    Review
                  </Button>
                </Stack>
              )}

              {mode === "edit" && isReviewing && (
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2} my={5} sx={{ borderBottom: "0.5px solid", borderColor: "divider", pb: 2 }}>
                  <Box>
                    <Button variant="contained" disableElevation onClick={() => setIsReviewing(false)}>
                      EDIT
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      onClick={() => { setOpenConfirmation(true); }}
                      disabled={!ableToCreate}
                      variant="contained"
                      disableElevation
                      sx={{ maxWidth: 300, mr: 2 }}
                    >
                      Save Changes
                    </Button>
                    <ConfirmationDialog
                      open={openConfirmation}
                      onClose={handleCancelConfirmation}
                      onConfirm={handleSubmitConfirm}
                      title="Edit Confirmation"
                      content="Are you sure you want to confirm these edits?"
                    />
                  </Box>
                </Stack>
              )}

              {mode === "edit" && !isReviewing && (
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" my={5} spacing={1} sx={{ borderBottom: "0.5px solid", borderColor: "divider", pb: 2 }}>
                  {pdfData && (
                    <>
                      <Button
                        disabled={!checkFormValidity()}
                        disableElevation
                        variant="outlined"
                        sx={{ borderColor: "divider", color: "text.secondary", "&:hover": { borderColor: "text.primary", backgroundColor: "rgba(0,0,0,0.03)" } }}
                        onClick={() => {
                          if (
                            (!genInfo.ExitStrategy && genInfo.ExitStrategy !== 0) ||
                            !genInfo.ProjectedCloseDate ||
                            !contractSection.BrochurePrice
                          ) {
                            alert("Exit Strategy, Projected Closed Date, and Brochure price are required");
                            return;
                          }
                          if (genInfo.ExitStrategy && genInfo.ExitStrategy < -100) { alert("Exit strategy cannot be less than -100%"); return; }
                          if (genInfo.ExitStrategy && genInfo.ExitStrategy > 100) { alert("Exit strategy cannot be greater than 100%"); return; }
                          if (hasUnsavedChanges) {
                            setShowPrintWarning(true);
                          } else {
                            generatePDF(pdfData, genInfo, contractSection, paType, laborSection, materialSection, lotExtraSection, optionsSection, softCostSection, paType === "sold" ? forwardCommitmentSection : null, runningTotals, notesList);
                          }
                        }}
                      >
                        Print
                      </Button>
                      {printUnsavedDialog}
                    </>
                  )}
                  <Button
                    disableElevation
                    variant="contained"
                    onClick={() => {
                      if (ableToCreate) {
                        setIsReviewing(true);
                      } else {
                        alert("Please fill out the general information");
                      }
                    }}
                    sx={{ maxWidth: 300 }}
                    disabled={!checkFormValidity()}
                  >
                    Review
                  </Button>
                </Stack>
              )}

              {mode === "view" && (
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" my={5} spacing={1} sx={{ borderBottom: "0.5px solid", borderColor: "divider", pb: 2 }}>
                  {pdfData && (
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={() =>
                        generatePDF(pdfData, genInfo, contractSection, paType, laborSection, materialSection, lotExtraSection, optionsSection, softCostSection, paType === "sold" ? forwardCommitmentSection : null, runningTotals, notesList)
                      }
                    >
                      Print
                    </Button>
                  )}
                </Stack>
              )}

              {/* ── Job info form ──────────────────────────────────────────── */}
              <Grid container spacing={2} my={5}>
                {/* Subdivision / Address autocompletes — create only */}
                {mode === "create" && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        disablePortal
                        options={subdivisions.filter((sub) =>
                          sub && jobs.filter((j) => j.subdivision === sub && !j.closeDate && j.profitAnalysisId === 0 && j.role === "Write").length > 0
                        ).sort()}
                        ref={subdivisionRef}
                        disabled={isReviewing || !!locationStateSubdivision}
                        value={
                          genInfo.Subdivision && subdivisions.includes(genInfo.Subdivision)
                            ? genInfo.Subdivision
                            : (genInfo.Subdivision ? genInfo.Subdivision : "")
                        }
                        onChange={handleSubdivisionChange}
                        isOptionEqualToValue={(option, value) => option === value}
                        renderInput={(params) => (
                          <TextField {...params} label="Subdivision*" />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        disablePortal
                        key={selectedJob?.jobAddress}
                        disabled={isReviewing || !!locationStateJobAddress}
                        filterOptions={filterOptions}
                        options={jobs.filter((j) => j.subdivision === genInfo.Subdivision && j.profitAnalysisId === 0 && !j.closeDate && j.role === "Write")}
                        getOptionLabel={(option) => option.jobAddress}
                        value={selectedJob}
                        onChange={(_event: any, newValue: JobProps | null) => {
                          setGenInfo((prev) => ({ ...prev, StreetAddress: newValue ? newValue.jobAddress : "" }));
                          setSelectedJob(newValue ?? undefined);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Address*"
                            helperText={!genInfo.Subdivision ? "Please select a subdivision first" : ""}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}

                {/* Subdivision / Address — edit / view (disabled text fields) */}
                {mode !== "create" && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField value={genInfo.Subdivision} label="Subdivision*" variant="filled" disabled fullWidth
                        sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                        onChange={(e) => setGenInfo({ ...genInfo, Subdivision: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField value={genInfo.StreetAddress} label="Address*" variant="filled" disabled fullWidth
                        sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                        onChange={(e) => setGenInfo({ ...genInfo, StreetAddress: e.target.value })} />
                    </Grid>
                  </>
                )}

                {/* Project # / Job # */}
                <Grid item xs={12} sm={3}>
                  <TextField value={genInfo.ProjectNumber} label="Project Number" variant="filled" disabled fullWidth
                    sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                    onChange={(e) => setGenInfo({ ...genInfo, ProjectNumber: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField value={genInfo.JobNumber} label="Job Number" variant="filled" disabled fullWidth
                    sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                    onChange={(e) => setGenInfo({ ...genInfo, JobNumber: e.target.value })} />
                </Grid>

                {/* Stage Code — create uses xs={6}, view adds a leading space in original; kept as xs={6} */}
                <Grid item xs={12} sm={6}>
                  <TextField value={genInfo.StageCode} label="Stage Code" variant="filled" disabled fullWidth
                    sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                    onChange={(e) => setGenInfo({ ...genInfo, StageCode: e.target.value })} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField value={genInfo.Buyer} label="Buyer" variant="filled" disabled fullWidth
                    sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                    onChange={(e) => setGenInfo({ ...genInfo, Buyer: e.target.value })} />
                </Grid>

                {/* Plan / Elevation */}
                {mode === "create" ? (
                  <Grid item xs={12} sm={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField value={genInfo.PlanNumber} label="Plan" variant="filled" disabled fullWidth
                          sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                          onChange={(e) => setGenInfo({ ...genInfo, PlanNumber: e.target.value })} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField value={genInfo.PlanElevationNumber} label="Elevation" variant="filled" disabled fullWidth
                          sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                          onChange={(e) => setGenInfo({ ...genInfo, PlanElevationNumber: e.target.value })} />
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6}>
                    <TextField value={genInfo.PlanNumber} label="Plan" variant="filled" disabled
                      sx={{ width: "48%", "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                      onChange={(e) => setGenInfo({ ...genInfo, PlanNumber: e.target.value })} />
                    <TextField value={genInfo.PlanElevationNumber} label="Elevation" variant="filled" disabled
                      sx={{ width: "48%", marginLeft: 1, "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                      onChange={(e) => setGenInfo({ ...genInfo, PlanElevationNumber: e.target.value })} />
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <TextField value={genInfo.JobType} label="Job Type" variant="filled" disabled fullWidth
                    sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                    onChange={(e) => setGenInfo({ ...genInfo, JobType: e.target.value })} />
                </Grid>

                {/* Date Sold — sold only */}
                {paType === "sold" && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      value={
                        genInfo.DateSold
                          ? moment(genInfo.DateSold).format("MM/DD/YYYY")
                          : ""
                      }
                      label="Date Sold"
                      variant="filled"
                      disabled
                      fullWidth
                      sx={{ "& .MuiFilledInput-root": { backgroundColor: "rgba(0,0,0,0.03)", borderRadius: 1 } }}
                    />
                  </Grid>
                )}

                {/* Exit Strategy / Projected Close Date / BP% / BL% */}
                {mode === "view" ? (
                  // View: all disabled, uses plain TextField for exit strategy and plain DatePicker
                  <>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        minDate={minDate}
                        disabled
                        value={genInfo.ProjectedCloseDate}
                        label="Projected Closed Date*"
                        sx={{ width: "100%" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} />
                    <Grid item xs={12} sm={6} />
                    <Grid item xs={12} sm={6}>
                      <TextField
                        value={genInfo.ExitStrategy ?? ""}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        label="Exit Strategy*"
                        disabled
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} />
                    {paType === "spec" && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            value={genInfo.BPPercent ?? ""}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            label="BP %*"
                            disabled
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} />
                        <Grid item xs={12} sm={6}>
                          <TextField
                            value={genInfo.BLPercent ?? ""}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            label="BL %*"
                            disabled
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} />
                      </>
                    )}
                  </>
                ) : (
                  // Create / Edit: editable PercentField + SafeDatePicker
                  <>
                    <Grid item xs={12} sm={6}>
                      <SafeDatePicker
                        label="Projected Closed Date*"
                        value={genInfo.ProjectedCloseDate ?? null}
                        onChange={(v) => {
                          setGenInfo({ ...genInfo, ProjectedCloseDate: v });
                          if (mode === "edit") setProjectedCloseDateFieldUpdated(true);
                        }}
                        minDate={minDate}
                        disabled={isReviewing}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <PercentField
                        label="Exit Strategy*"
                        value={genInfo.ExitStrategy ?? null}
                        onChange={(v) => {
                          setGenInfo({ ...genInfo, ExitStrategy: v ?? undefined });
                          if (v === null || v === 0 || v < -100 || v > 100) {
                            setExitStrategyError(true);
                          } else {
                            setExitStrategyError(false);
                            if (mode === "edit") setExitStrategyFieldUpdated(true);
                          }
                        }}
                        disabled={isReviewing}
                        required
                        tooltip="Exit Strategy value must be between -100 and 100."
                        error={exitStrategyError}
                        helperText={exitStrategyError ? "Exit Strategy must be within the range of -100 to 100, and not a zero" : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} />
                    {paType === "spec" && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <PercentField
                            label="BP %*"
                            value={genInfo.BPPercent ?? null}
                            onChange={(v) => {
                              setGenInfo({ ...genInfo, BPPercent: v ?? undefined });
                              setBpPercentError(v === null || v === 0 || v < -100 || v > 100);
                            }}
                            disabled={isReviewing}
                            required
                            tooltip="BP % value must be between -100 and 100."
                            error={bpPercentError}
                            helperText={bpPercentError ? "BP % must be within the range of -100 to 100, and not a zero" : ""}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} />
                        <Grid item xs={12} sm={6}>
                          <PercentField
                            label="BL %*"
                            value={genInfo.BLPercent ?? null}
                            onChange={(v) => {
                              setGenInfo({ ...genInfo, BLPercent: v ?? undefined });
                              setBlPercentError(v === null || v === 0 || v < -100 || v > 100);
                            }}
                            disabled={isReviewing}
                            required
                            tooltip="BL % value must be between -100 and 100."
                            error={blPercentError}
                            helperText={blPercentError ? "BL % must be within the range of -100 to 100, and not a zero" : ""}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} />
                      </>
                    )}
                  </>
                )}
              </Grid>

              {/* ── Change History (edit / view) ───────────────────────────── */}
              {(mode === "edit" || mode === "view") && (
                <Box pt={3} id="changeHistory">
                  <Accordion elevation={0} expanded={changeHistoryExpanded}
                    onChange={() => setChangeHistoryExpanded(!changeHistoryExpanded)}
                    sx={{
                      border: "0.5px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      "&:before": { display: "none" },
                      "&.Mui-expanded": { margin: 0 }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "rgba(0,0,0,0.02)", borderBottom: changeHistoryExpanded ? "0.5px solid" : "none", borderColor: "divider" }}>
                      <Box sx={{ display: "flex", flex: 1, justifyContent: "space-between", alignItems: "center" }}>
                        <Stack direction="row" spacing={2}>
                          <Icon fontSize="large">
                            <ChangeHistoryIcon fontSize="large" sx={{ color: "#ACACAC" }} />
                          </Icon>
                          <Typography fontSize={20} sx={{ alignSelf: "end" }}>Change History</Typography>
                        </Stack>
                        <Typography fontSize={24} sx={{ alignSelf: "end", color: "#ACACAC", fontWeight: "bold" }} />
                        <Typography fontSize={24} sx={{ alignSelf: "end", color: "#ACACAC", fontWeight: "bold" }} />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Grid container mt={4} spacing={2}>
                          <Grid item xs={6} sx={{ borderRight: "1px solid #ccc" }}>
                            <Grid item xs={12}>
                              <Typography variant="h6" fontWeight="normal" sx={{ mb: 2 }}>Projected Close Date Changes</Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={4}><Typography variant="body2" color="text.secondary" fontWeight={600}>Projected Close Date</Typography></Grid>
                                <Grid item xs={8}><Typography variant="body2" color="text.secondary" fontWeight={600}>Last Updated</Typography></Grid>
                                {projectedCloseDateChanges.map((change, index) => (
                                  <React.Fragment key={index}>
                                    <Grid item xs={4}>
                                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                        {moment(change.projectedCloseDate).format("MM/DD/YYYY")}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={8}>
                                      <Typography variant="body2" color="text.secondary" fontWeight={400}>
                                        {moment(change.updatedOn).format(mode === "edit" ? "MM/DD/YYYY HH:mm A" : "MM/DD/YYYY")} by {change.updatedBy}
                                      </Typography>
                                    </Grid>
                                  </React.Fragment>
                                ))}
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={6} sx={{ display: "flex", flexDirection: "column" }}>
                            <Grid item xs={12}>
                              <Typography variant="h6" fontWeight="normal" sx={{ mb: 2 }}>Exit Strategy Changes</Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={3}><Typography variant="body2" color="text.secondary" fontWeight={600}>Exit Strategy %</Typography></Grid>
                                <Grid item xs={9}><Typography variant="body2" color="text.secondary" fontWeight={600}>Last Updated</Typography></Grid>
                                {exitStrategyChanges.map((change, index) => (
                                  <React.Fragment key={index}>
                                    <Grid item xs={3}>
                                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                        {change.exitStrategy}%
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={9}>
                                      <Typography variant="body2" color="text.secondary" fontWeight={400}>
                                        {moment(change.updatedOn).format("MM/DD/YYYY")} by {change.updatedBy}
                                      </Typography>
                                    </Grid>
                                  </React.Fragment>
                                ))}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  {!changeHistoryExpanded && <Divider />}
                </Box>
              )}

              {/* ── Section components ─────────────────────────────────────── */}
              <ContractSection
                state={contractSection}
                setState={setContractSection}
                formErrors={formErrors}
                updateField={updateField}
                total={runningTotals.contract}
                fullRetail={runningTotals.fullRetail}
                mode={sectionMode}
                pageVariant={mode}
                paType={paType}
                discountToBP={Math.round(runningTotals.discountToBP)}
                bpPrice={Math.round(runningTotals.bpPrice)}
                discountToBL={Math.round(runningTotals.discountToBL)}
                blPrice={Math.round(runningTotals.blPrice)}
                {...(mode !== "create" ? { initialState: initialContractSection, changeCount: changeCountContractSection } : {})}
              />
              <LaborSection
                state={laborSection}
                setState={setLaborSection}
                formErrors={formErrors}
                updateField={updateField}
                total={runningTotals.labor}
                mode={sectionMode}
                pageVariant={mode}
                {...(mode !== "create" ? { initialState: initialLaborSection, changeCount: changeCountLaborSection } : {})}
              />
              <MaterialSection
                state={materialSection}
                setState={setMaterialSection}
                formErrors={formErrors}
                updateField={updateField}
                total={runningTotals.material}
                mode={sectionMode}
                pageVariant={mode}
                {...(mode !== "create" ? { initialState: initialMaterialSection, changeCount: changeCountMaterialSection } : {})}
              />
              <LotExtrasSection
                state={lotExtraSection}
                setState={setLotExtraSection}
                formErrors={formErrors}
                updateField={updateField}
                total={runningTotals.lotExtras}
                subtotalLaborMaterial={subtotalLaborMaterial}
                mode={sectionMode}
                pageVariant={mode}
                {...(mode !== "create" ? { initialState: initialLotExtraSection, changeCount: changeCountLotExtraSection } : {})}
              />
              <OptionsSection
                state={optionsSection}
                setState={setOptionsSection}
                formErrors={formErrors}
                updateField={updateField}
                total={runningTotals.options}
                totalLaborMaterial={totalLaborMaterial}
                mode={sectionMode}
                pageVariant={mode}
                {...(mode !== "create" ? { initialState: initialOptionsSection, changeCount: changeCountOptionsSection } : {})}
              />
              <SoftCostSection
                state={softCostSection}
                setState={setSoftCostSection}
                formErrors={formErrors}
                updateField={updateField}
                total={runningTotals.softCost}
                totalBP={runningTotals.softCostBP}
                totalBL={runningTotals.softCostBL}
                mode={sectionMode}
                pageVariant={mode}
                marketAdjustment={marketAdjustment}
                paType={paType}
                blPercent={genInfo.BLPercent}
                contractPrice={contractSection.BrochurePrice || 0}
                bpRealtorCommissions={runningTotals.bpRealtorCommissions || 0}
                bpInHouseCommissions={runningTotals.bpInHouseCommissions || 0}
                bpBuilderBonus={runningTotals.bpBuilderBonus || 0}
                blBuilderBonus={runningTotals.blBuilderBonus || 0}
                blInHouseCommissions={runningTotals.blInHouseCommissions || 0}
                blRealtorCommissions={runningTotals.blRealtorCommissions || 0}
                disableCommissions={paType === "spec"}
                {...(mode !== "create" ? { initialState: initialSoftCostSection, changeCount: changeCountSoftCostSection } : {})}
              />

              {/* Forward Commitment — sold only */}
              {paType === "sold" && (
                <ForwardCommitmentSection
                  state={forwardCommitmentSection}
                  setState={setForwardCommitmentSection}
                  mode={sectionMode}
                  minDate={minDate}
                  pageVariant={mode}
                  {...(mode !== "create" ? { initialState: initialForwardCommitmentSection, changeCount: changeCountForwardCommitmentSection } : {})}
                />
              )}

              {/* ── Notes ─────────────────────────────────────────────────── */}
              <Box pt={mode === "view" ? 0 : 3} py={mode === "view" ? 3 : undefined} id="notes">
                <Accordion elevation={0} expanded={notesExpanded} onChange={() => setNotesExpanded(!notesExpanded)}
                  sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 1, "&:before": { display: "none" }, "&.Mui-expanded": { margin: 0 } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "rgba(0,0,0,0.02)", borderBottom: notesExpanded ? "0.5px solid" : "none", borderColor: "divider" }}>                    <Box sx={{ display: "flex", flex: 1, justifyContent: "space-between", alignItems: "center" }}>
                    <Stack direction="row" spacing={2}>
                      <Icon fontSize="large">
                        <CommentIcon fontSize="large" sx={{ color: "#ACACAC" }} />
                      </Icon>
                      <Typography fontSize={20} sx={{ alignSelf: "end" }}>Notes</Typography>
                    </Stack>
                  </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ mt: 2 }}>
                      {mode === "create" ? (
                        // Create: Add Note button (batched with save)
                        <>
                          <TextField
                            disabled={isReviewing}
                            variant="outlined"
                            size="small"
                            multiline
                            rows={4}
                            placeholder="Add any additional notes here..."
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            fullWidth
                          />
                          {!isReviewing && (
                            <Box display="flex" justifyContent="flex-end" mt={2}>
                              <Button
                                variant="contained"
                                disableElevation
                                onClick={() => {
                                  if (String(note).trim()) {
                                    setNotesList([{
                                      profitAnalysisId: 0,
                                      jobId: genInfo.JobId,
                                      profitAnalysisNoteId: 0,
                                      note: String(note).trim(),
                                      createdBy: account.name as string,
                                      createdOn: moment().format(),
                                    }, ...notesList]);
                                    setNote("");
                                  }
                                }}
                                disabled={!String(note).trim()}
                              >
                                Add Note
                              </Button>
                            </Box>
                          )}
                        </>
                      ) : (
                        // Edit / View: Save Note immediately via API
                        <>
                          <TextField
                            disabled={isReviewing}
                            variant="outlined"
                            multiline
                            rows={4}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Enter any additional notes or comments here..."
                            fullWidth
                            onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handleSaveNotes(); }}
                          />
                          <Box display="flex" justifyContent="flex-end" mt={2}>
                            <Button
                              disabled={isReviewing || !String(note).trim()}
                              variant="contained"
                              disableElevation
                              onClick={handleSaveNotes}
                              startIcon={<CommentIcon />}
                            >
                              Save Note
                            </Button>
                          </Box>
                        </>
                      )}
                    </Box>
                    <Box>
                      {notesList.length > 0 ? (
                        notesList.map((noteItem, index) => (
                          <Box
                            key={mode === "create" ? index : noteItem.profitAnalysisNoteId}
                            sx={{ mb: 2, p: 2, borderRadius: 1, bgcolor: grey[50], border: `1px solid ${grey[200]}` }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {noteItem.createdBy} -{" "}
                              {moment(noteItem.createdOn).format("MMM D, YYYY [at] h:mm A")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                              {noteItem.note}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        mode !== "create" && (
                          <Typography variant="body2" sx={{ fontStyle: "italic", color: grey[600] }}>
                            No notes available for this profit analysis.
                          </Typography>
                        )
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
                {!notesExpanded && <Divider />}
                {!notesExpanded && (
                  <Box sx={{ mt: 2 }}>
                    {mode === "create" ? (
                      <>
                        <TextField
                          disabled={isReviewing}
                          variant="outlined"
                          size="small"
                          multiline
                          rows={4}
                          placeholder="Add any additional notes here..."
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          fullWidth
                        />
                        {!isReviewing && (
                          <Box display="flex" justifyContent="flex-end" mt={2}>
                            <Button
                              variant="contained"
                              disableElevation
                              onClick={() => {
                                if (String(note).trim()) {
                                  setNotesList([{
                                    profitAnalysisId: 0,
                                    jobId: genInfo.JobId,
                                    profitAnalysisNoteId: 0,
                                    note: String(note).trim(),
                                    createdBy: account.name as string,
                                    createdOn: moment().format(),
                                  }, ...notesList]);
                                  setNote("");
                                }
                              }}
                              disabled={!String(note).trim()}
                            >
                              Add Note
                            </Button>
                          </Box>
                        )}
                      </>
                    ) : (
                      <>
                        <TextField
                          disabled={isReviewing}
                          variant="outlined"
                          multiline
                          rows={4}
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="Enter any additional notes or comments here..."
                          fullWidth
                          onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handleSaveNotes(); }}
                        />
                        <Box display="flex" justifyContent="flex-end" mt={2}>
                          <Button
                            disabled={isReviewing || !String(note).trim()}
                            variant="contained"
                            disableElevation
                            onClick={handleSaveNotes}
                            startIcon={<CommentIcon />}
                          >
                            Save Note
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>

              {/* ── Action bar: bottom ──────────────────────────────────────── */}
              {mode === "create" && isReviewing && (
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2} my={10} sx={{ borderBottom: "0.5px solid", borderColor: "divider", pb: 2 }}>
                  <Box>
                    <Button variant="contained" disableElevation onClick={() => setIsReviewing(false)}>
                      EDIT
                    </Button>
                  </Box>
                  <Box>
                    <Button onClick={() => { handleCreate(false); }} disabled={!ableToCreate} variant="contained" disableElevation sx={{ maxWidth: 300, mr: 2 }}>
                      Submit
                    </Button>
                    <Button variant="contained" disableElevation sx={{ maxWidth: 300 }} onClick={() => { handleCreate(true); }} disabled={!ableToCreate}>
                      Save and create another
                    </Button>
                  </Box>
                </Stack>
              )}

              {mode === "create" && !isReviewing && (
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" spacing={1} my={10} sx={{ borderBottom: "0.5px solid", borderColor: "divider", pb: 2 }}>
                  <Button
                    disableElevation
                    variant="outlined"
                    onClick={handlePrint}
                    disabled={printDisabled}
                    sx={{
                      borderColor: "divider",
                      color: "text.secondary",
                      "&:hover": {
                        borderColor: "text.primary",
                        backgroundColor: "rgba(0,0,0,0.03)"
                      }
                    }}
                  >
                    Print
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      if (ableToCreate) { setIsReviewing(true); } else { alert("Please fill out the general information"); }
                    }}
                    sx={{ maxWidth: 300 }}
                    disabled={!checkFormValidity()}
                  >
                    Review
                  </Button>
                </Stack>
              )}

              {mode === "edit" && isReviewing && (
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2} my={10} sx={{ borderBottom: "0.5px solid", borderColor: "divider", pb: 2 }}>
                  <Box>
                    <Button disableElevation variant="contained" onClick={() => setIsReviewing(false)}>
                      EDIT
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      disableElevation
                      onClick={() => { setOpenConfirmation(true); }}
                      disabled={!ableToCreate}
                      variant="contained"
                      sx={{ maxWidth: 300, mr: 2 }}
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Stack>
              )}

              {mode === "edit" && !isReviewing && (
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" my={10} sx={{ borderBottom: "0.5px solid", borderColor: "divider", pb: 2 }}>
                  <Button
                    disableElevation
                    variant="contained"
                    onClick={() => {
                      if (ableToCreate) { setIsReviewing(true); } else { alert("Please fill out the general information"); }
                    }}
                    sx={{ maxWidth: 300 }}
                    disabled={!checkFormValidity()}
                  >
                    Review
                  </Button>
                </Stack>
              )}
            </Box>
            <SectionNav items={navbarItems} drawerWidth={drawerWidth} />
          </Box>
          {mode === "create" && jobsLoading && (
            <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
              <CircularProgress />
            </Box>
          )}
        </Container>
      </LocalizationProvider>
    </div>
  );
}
